import {
  AcceptRejectDeferredPromise,
  BlindTransferDeferredPromise,
  ConferenceDeferredPromise,
  DeferredPromise,
  HangupDeferredPromise,
  HoldToggleDeferredPromise,
  LogoutDeferredPromise,
  MuteToggleDeferredPromise,
  OutboundDialDeferredPromise,
  ParticipantDeferredPromise,
  PendingMergeDeferredPromise,
  PresenceDeferredPromise,
  AgentStatusEchoDeferredPromise
} from '../../Model/Classes/DeferredPromise';
import {
  AgentStatusInfo,
  CallInfo,
  CallResult,
  Constants,
  Contact,
  GenericResult,
  HangupResult,
  HoldToggleResult,
  MuteToggleResult,
  ParticipantResult,
  PhoneCall,
  PhoneCallAttributes,
  SharedCapabilitiesResult,
  VoiceCapabilitiesResult,
} from '@salesforce/scv-connector-base';
import {
  CHANNEL_TYPES,
  CONTEXTUAL_OPERATION_TYPE,
  ERROR_CODE,
  IActivity,
  IContextualContact,
  IGetPresenceResult,
  IInteraction,
  INTERACTION_DIRECTION_TYPES,
  INTERACTION_STATES,
  ISupportedChannel,
  LOG_LEVEL,
  Logger,
  NOTIFICATION_TYPE,
  SearchLayouts,
  SearchRecords,
  clickToAct,
  clickToDial,
  getAppName,
  getLiveAgents,
  getPresence,
  initializeComplete,
  loadBridgeScripts,
  logout,
  registerEnableClickToDial,
  registerGetSearchLayout,
  registerIsToolbarVisible,
  registerOnInteraction,
  registerOnLogout,
  registerOnPresenceChanged,
  registerSetSupportedChannels,
  sendNotification,
  setAppHeight
} from '@amc-technology/davinci-api';
import { Component, OnInit } from '@angular/core';
import { deepCopy, generateCID } from 'src/app/utils';
import { saturateAmcCall, saturateParticipantResult, saturatePhoneCall } from 'src/app/Model/Classes/ScvObjectSaturation';

import { AMC_EVENT_NAMES } from '../../bridge/constants';
import { ActiveParty } from 'src/app/Model/Types/ActiveParty';
import { AmcCall } from '../../Model/Classes/AmcCall';
import { BridgeEventsService } from '@amc-technology/applicationangularframework';
import { CID } from 'src/app/Model/Types/CID';
import { CommunicationService } from '../../services/communication.service';
import { Ensure } from 'src/app/Model/Types/Ensure';
import { IAddParticipantPayload } from 'src/app/Model/Interfaces/IAddParticipantPayload';
import { IAgentStatusPayload } from 'src/app/Model/Interfaces/IAgentStatusPayload';
import { IEndCallPayload } from 'src/app/Model/Interfaces/IEndCallPayload';
import { IEventQueue, EventQueueType, IEventQueuePresence, IEventQueueInteraction } from 'src/app/Model/Interfaces/IEventQueue';
import { IGetContactsPayload } from 'src/app/Model/Interfaces/IGetContactsPayload';
import { ISoftPhoneState } from '../../Model/Interfaces/ISoftPhoneState';
import { ISwapCallsPayload } from 'src/app/Model/Interfaces/ISwapCallsPayload';
import { LoggerService } from '../../services/logger.service';
import { ScvVoiceEventPayload } from 'src/app/Model/Types/ScvVoiceEventPayload';
import { StorageService } from '../../services/storage.service';
import { StudioConfigService } from '../../services/studio-config.service';
import { Subject } from 'rxjs';
import { VoiceCallRecordCreationEnum } from 'src/app/Model/Enums/VoiceCallRecordCreationEnum';

@Component({
  selector: 'app-home',
  templateUrl: './home-byot.component.html',
  styleUrls: ['./home-byot.component.css']
})
export class HomeBYOTComponent implements OnInit {
  log: (
    logLevel: LOG_LEVEL,
    fName: string,
    message: string,
    object?: any,
    errorCode?: ERROR_CODE,
    localTime?: Date
  ) => void;

  debugMode = false;

  private readonly presencesFromSalesforce: Map<string, string> = new Map();
  private readonly promisesToResolve: DeferredPromise[] = [];
  private currentPresence?: string;
  private currentReason?: string;
  private ctiId?: string;
  private loggedIn = false;

  private readonly state: ISoftPhoneState = {
    activeCalls: {},
    expiredCalls: [],
    agentStatus: '',
  };

  private readonly logger: Logger;
  private bridgeScripts: string[];
  private bridgeEventsService?: BridgeEventsService;
  private readonly bridgeLogger: Logger;

  state$: Subject<ISoftPhoneState> = new Subject<ISoftPhoneState>();
  private appName?: string;
  private readonly cname = 'Home.';

  activeCalls: AmcCall[] = [];
  activeCallsCopy: AmcCall[] = []; // ToDo: use this for getActiveCalls
  activeParties: ActiveParty[] = [];
  public isInitializing = true;

  private eventQueue: IEventQueue[] = [];
  private uniqueInteractionIds: string[] = [];
  private prevActiveCalls: AmcCall[] = [];
  private prevActiveParties: ActiveParty[] = [];
  private prevActiveCleared = false;
  private localStorageCheckTimeoutId: ReturnType<typeof setTimeout> | undefined;

  private pendingMergePromise: PendingMergeDeferredPromise | undefined;

  private ignoredInteractionId: string | undefined;

  private getActiveCallsCounter = 0;

  constructor(
    private readonly loggerService: LoggerService,
    private readonly storageService: StorageService,
    private readonly scs: StudioConfigService,
    private readonly communicationService: CommunicationService
  ) {
    this.bridgeScripts = [];
    this.log = this.loggerService.log.bind(this.loggerService);
    this.logger = this.loggerService.logger;
    this.bridgeLogger = this.logger;
    this.storageService.stateSubscriber = this.state$.subscribe(
      this.storageService.setState.bind(this.storageService)
    );
  }

  private async initOnLoad(): Promise<void> {
    const functionName = 'Home.initOnLoad';
    try {
      this.log(LOG_LEVEL.Trace, functionName, 'Initializing home component');
      const bridgeUrl: string =
        window.location.origin + '/bridge-dist/bridge.bundle.js';
      this.bridgeScripts = this.bridgeScripts.concat([
        `${bridgeUrl}?appname=${encodeURIComponent(await getAppName())}`,
      ]);

      await this.parentNgOnInit();

      this.registerForBridgeEvents();
      await this.setup();

      // ! Initialize Bridge Connector
      this.bridgeEventsService?.sendEvent(AMC_EVENT_NAMES.INITIALIZE_CONNECTOR);
      this.checkInitialAgentStatus();
      this.log(LOG_LEVEL.Trace, functionName, 'Initialized home component');
    } catch (error) {
      this.log(
        LOG_LEVEL.Error,
        functionName,
        'Failed to initialize home component',
        error
      );
    }
  }

  ngOnInit(): void {
    const functionName = 'Home.' + 'ngOnInit';
    try {
      this.loadCallsAndPartiesFromLocalStorage();
      this.initOnLoad();
    } catch (error) {
      this.log(
        LOG_LEVEL.Error,
        functionName,
        'Failed to initialize home component',
        error
      );
    }
  }

  getSalesforceConfig(salesforceConfigs: {
    presences: any;
    callCenterApiName: string;
    orgUrl: string;
    orgId: string;
    agentId: string;
  }): void {
    const functionName = 'Home.' + 'getSalesforceConfig';
    try {
      this.log(LOG_LEVEL.Debug, functionName, 'Configs from Salesforce', salesforceConfigs);

      this.log(LOG_LEVEL.Debug, functionName, 'Populating Presence Map', salesforceConfigs.presences);
      const presenceMap = salesforceConfigs.presences;
      const presenceKeys = Object.keys(presenceMap);
      for (const presenceKey of presenceKeys) {
        const currentPresence = presenceMap[presenceKey];
        this.presencesFromSalesforce.set(
          currentPresence['statusName'],
          currentPresence['statusId']
        );
      }
      this.log(LOG_LEVEL.Debug, functionName, 'Presence Map', this.presencesFromSalesforce);

      this.communicationService.initialize(
        salesforceConfigs.orgId,
        salesforceConfigs.orgUrl,
        salesforceConfigs.callCenterApiName,
        salesforceConfigs.agentId
      );
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Failed to get salesforce configs', error);
    }
  }

  sendErrorNotification(errorMessage: any): void {
    const functionName = 'sendErrorNotification';
    try {
      this.log(LOG_LEVEL.Error, functionName, 'Error Notification', errorMessage);
      sendNotification(errorMessage, NOTIFICATION_TYPE.Error);
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Failed to send error notification', error);
    }
  }

  async checkInitialAgentStatus(): Promise<void> {
    const functionName = 'Home.' + 'checkInitialAgentStatus';
    try {
      const currentPresence: IGetPresenceResult = await getPresence();
      this.log(
        LOG_LEVEL.Loop,
        functionName,
        'Checking Initial presence',
        currentPresence
      );
      if (
        !this.loggedIn &&
        currentPresence.presence.toLowerCase() !== 'pending'
      ) {
        this.log(
          LOG_LEVEL.Debug,
          functionName,
          'Sending Login event to Salesforce'
        );
        this.bridgeEventsService?.sendEvent(AMC_EVENT_NAMES.LOGIN);
        this.receivePresenceFromFramework(
          currentPresence.presence,
          currentPresence.reason,
          'Initial Presence'
        );
        this.loggedIn = true;
      } else {
        this.log(
          LOG_LEVEL.Debug,
          functionName,
          'Presence is pending. Login window will close when the presence changes.'
        );
      }
      registerOnPresenceChanged(this.receivePresenceFromFramework.bind(this));
    } catch (error) {
      this.log(
        LOG_LEVEL.Error,
        functionName,
        'Failed to check agent status',
        error
      );
    }
  }

  registerForBridgeEvents(): void {
    const functionName = 'Home.' + 'registerForBridgeEvents';
    try {
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.LOGIN_FRAME_CLOSED,
        this.loginFrameClosed.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.LOG,
        this.logFromBridge.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.LOGOUT,
        this.logoutUser.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.GET_SALESFORCE_CONFIG,
        this.getSalesforceConfig.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.SEND_ERROR_NOTIFICATION,
        this.sendErrorNotification.bind(this)
      );
      // State Management Events
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.GET_ACTIVE_CALLS,
        this.getActiveCalls.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.GET_AGENT_STATUS,
        this.getAgentStatus.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.PRESENCE_CHANGE,
        this.onPresenceChangeFromScv.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.GET_PHONE_CONTACTS,
        this.getPhoneContacts.bind(this)
      );
      // Call Control Events
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.ACCEPT,
        this.acceptCallEvent.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.REJECT,
        this.rejectCallEvent.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.HANGUP,
        this.hangupOrCompleteWarmTransfer.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.MUTE,
        this.muteCallEvent.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.UNMUTE,
        this.unmuteCallEvent.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.HOLD,
        this.holdCallEvent.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.UNHOLD,
        this.unholdCallEvent.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.ADD_PARTICIPANT,
        this.addParticipant.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.BLIND_TRANSFER,
        this.blindTransfer.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.CONFERENCE,
        this.conference.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.DIAL,
        this.sendOutbound.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.SEND_DIGITS,
        this.sendDigits.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.GET_VOICE_CAPABILITIES,
        this.getVoiceCapabilities.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.GET_SHARED_CAPABILITIES,
        this.getSharedCapabilities.bind(this)
      );
      this.bridgeEventsService?.subscribe(
        AMC_EVENT_NAMES.SWAP,
        this.swapCalls.bind(this)
      );
    } catch (error) {
      this.log(
        LOG_LEVEL.Error,
        functionName,
        'Failed to register for bridge events',
        error
      );
    }
  }

  async loginFrameClosed(isClosed: boolean): Promise<void> {
    const functionName = 'Home.' + 'loginFrameClosed';
    try {
      this.log(LOG_LEVEL.Debug, functionName, 'Login frame closed:', isClosed);

      this.isInitializing = !isClosed;
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error handling login frame closed event', error);
    } finally {
      this.log(LOG_LEVEL.Debug, functionName, 'END <<<');
    }
  }

  async swapCalls(data: ISwapCallsPayload): Promise<HoldToggleResult> {
    const fname = this.cname + 'swapCalls';

    try {
      this.log(LOG_LEVEL.Debug, fname, 'START >>>');

      const call1 = this.activeCalls.find(ac => ac.callId == data.call1.callId);
      const call2 = this.activeCalls.find(ac => ac.callId == data.call2.callId);

      if (!call1 || !call2) {
        throw new Error('No active call found for call1 or call2');
      }
      if (call1.callAttributes.isOnHold === call2.callAttributes.isOnHold) {
        throw new Error('Both calls are held or unheld. Cannot \'swap\'');
      }


      const heldCall = call1.callAttributes.isOnHold ? call1 : call2;
      const unheldCall = call1.callAttributes.isOnHold ? call2 : call1;

      await this.holdCallEvent(unheldCall);
      return this.unholdCallEvent(heldCall);

    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);

      throw err;
    } finally {
      this.log(LOG_LEVEL.Debug, fname, 'END <<<');
    }
  }

  async getSharedCapabilities(): Promise<SharedCapabilitiesResult> {
    const fname = this.cname + 'getSharedCapabilities';

    try {
      this.log(LOG_LEVEL.Debug, fname, 'START >>>');

      return new SharedCapabilitiesResult({
        debugEnabled: true,
        hasContactSearch: true,
        hasAgentAvailability: true,
        hasQueueWaitTime: false,
        hasTransferToOmniFlow: false,
        hasPendingStatusChange: true,
        hasSFDCPendingState: false,
        hasAutoAcceptEnabled: false
      });
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);

      throw err;
    } finally {
      this.log(LOG_LEVEL.Debug, fname, 'END <<<');
    }
  }

  async getVoiceCapabilities(): Promise<VoiceCapabilitiesResult> {
    const fname = this.cname + 'getVoiceCapabilities';

    try {
      this.log(LOG_LEVEL.Debug, fname, 'START >>>');

      return new VoiceCapabilitiesResult({
        hasMute: false,
        hasRecord: false,
        hasMerge: true,
        hasSwap: true,
        hasBlindTransfer: true,
        hasSignedRecordingUrl: false,
        supportsMos: false,
        hasSupervisorListenIn: false,
        hasSupervisorBargeIn: false,
        hasPhoneBook: true,
        hasGetExternalSpeakerDeviceSetting: false,
        hasSetExternalSpeakerDeviceSetting: false,
        hasGetExternalMicrophoneDeviceSetting: false,
        hasSetExternalMicrophoneDeviceSetting: false
      });
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);

      throw err;
    } finally {
      this.log(LOG_LEVEL.Debug, fname, 'END <<<');
    }
  }

  async setup(): Promise<void> {
    const functionName = 'Home.' + 'setup';
    try {
      const configs = {
        addCallerEnabled: this.scs.addCallerEnabled,
        conferenceEnabled: this.scs.conferenceEnabled,
        holdEnabled: this.scs.holdEnabled,
        muteEnabled: this.scs.muteEnabled,
        removeParticipantVariant: this.scs.removeParticipantVariant,
        swapEnabled: this.scs.swapEnabled,
        rejectEnabled: this.scs.rejectEnabled,
        keypadEnabled: this.scs.keypadEnabled,
        queueMapping: this.scs.ctiIdToSalesforceIdMap,
        queueWaitTime: this.scs.queueWaitTime,
        maxLoginAttempts: this.scs.maxLoginAttempts,
        loginAttemptFrequencyMs: this.scs.loginAttemptFrequencyMs,
      };
      this.log(
        LOG_LEVEL.Debug,
        functionName,
        'Sending configs to bridge',
        configs
      );
      await this.bridgeEventsService?.sendEvent(
        AMC_EVENT_NAMES.SEND_STUDIO_CONFIGS,
        configs
      );
      this.log(LOG_LEVEL.Trace, functionName, 'Setup complete');
    } catch (error) {
      this.log(
        LOG_LEVEL.Error,
        functionName,
        'Failed to get studio configs',
        error
      );
    }
  }

  // Must disable warning as DaVinci API requires an async function
  // eslint-disable-next-line @typescript-eslint/require-await
  async getChannelConfig(
    originalAppName: string,
    channels: ISupportedChannel[]
  ): Promise<void> {
    const functionName = 'Home.' + 'getChannelConfig';
    try {
      this.log(
        LOG_LEVEL.Information,
        functionName,
        'Channel Configs',
        channels
      );
      this.ctiId = channels[0].id; // this is needed for Transcription/DEG
      this.communicationService.setCtiId(this.ctiId);
    } catch (error) {
      this.log(
        LOG_LEVEL.Error,
        functionName,
        'Failed to get channel configs',
        error
      );

      throw error;
    }
  }

  private extractPartyIds(interaction: Readonly<IInteraction>): string[] {
    const fname = this.cname + 'extractPartyId';

    try {
      this.log(LOG_LEVEL.Loop, fname, 'START >>>');

      let ret: string[] = [];

      let partyId: unknown;

      if (!interaction.details) {
        throw new Error('Interaction details field is empty.');
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      partyId = interaction.details.getPhone()?.Value;
      if (!partyId) {
        this.log(LOG_LEVEL.Trace, fname, `No phonenumber present. Extracting ${interaction.direction === INTERACTION_DIRECTION_TYPES.Inbound ? 'ANI' : 'DNIS'}`);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        partyId = interaction.details.getField(interaction.direction === INTERACTION_DIRECTION_TYPES.Inbound ? 'ANI' : 'DNIS')?.Value;

        if (!partyId) {
          this.log(LOG_LEVEL.Trace, fname, `Extracting ${interaction.direction === INTERACTION_DIRECTION_TYPES.Inbound ? 'ani' : 'dnis'}`);
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          partyId = interaction.details.getField(interaction.direction === INTERACTION_DIRECTION_TYPES.Inbound ? 'ani' : 'dnis')?.Value;
        }
      }

      if (!partyId) {
        this.log(LOG_LEVEL.Trace, fname, 'No ANI/DNIS present. Extracting \'Email\'');
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        partyId = interaction.details.getField('Email')?.Value;

        if (!partyId) {
          this.log(LOG_LEVEL.Trace, fname, 'Extracting \'email\'');
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          partyId = interaction.details.getField('email')?.Value;
        }
      }

      if (!partyId || typeof partyId !== 'string') {
        this.log(LOG_LEVEL.Error, fname, `Could not extract party Id {${String(partyId)}}, all applicable fields are undefined or invalid data type`);
        partyId = 'UNKNOWN';
      }
      if (typeof partyId !== 'string') {
        throw new Error('partyId is not a string.');
      }

      ret.push(partyId);

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const conferenceParties = interaction.details?.fields?.conferenceParties?.Value && JSON.parse(interaction.details?.fields?.conferenceParties.Value);

      if (conferenceParties) {
        // ! If there are conference parties we want to only use them as the parties
        const parties: string[] = Object.values(conferenceParties).map(val => String(val));
        if (parties.length > 0) {
          this.log(LOG_LEVEL.Debug, fname, `${parties.length} parties found for interactionId: ${interaction.interactionId}`, parties);
          ret = [];
          ret.push(...parties);
        }
      }

      this.log(LOG_LEVEL.Trace, fname, 'Parties Extracted from interaction: ', ret);

      return ret;
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);

      throw err;
    } finally {
      this.log(LOG_LEVEL.Loop, fname, 'END <<<');
    }
  }

  private isInConference(interaction?: IInteraction): boolean {
    const fname = 'isInConference';
    let conference = false;
    try {

      if (interaction) {
        this.log(LOG_LEVEL.Trace, fname, 'Checking interaction for parties.', interaction);
        const parties = this.extractPartyIds(interaction);
        if (parties.length > 1) {
          this.log(LOG_LEVEL.Debug, fname, `Interaction has ${parties.length} parties on it. Assumed to be a conference.`);
          conference = true;
        }
      }

      const callState: boolean[] = this.activeCalls.map(ac => ac.callAttributes.isOnHold); // full list of held statuses
      const callSet: Set<boolean> = new Set(callState); // unique held statuses
      const intSet: Set<string> = new Set(this.activeParties.map(ap => ap.interactionId)); // number of unique interaction Ids

      if (
        callState.length > 1 &&
        (
          callSet.size === 1 || // all calls are in the same state
          intSet.size === 1 // all parties have the same interaction id
        )
      ) {
        this.log(LOG_LEVEL.Debug, fname, `Conference detected. ${callState.length} calls and ${callSet.size === 1 ? 'all calls are in the same state.' : 'all parties have the same interaction'}`);
        conference = true;
      } else if (callState.length > 2 && intSet.size < (callState.length - 1)) {
        // CVP conferences are an exception currently for Agent1. Call1 and Call2 will be merged into the same Interaction but Call3 will be a separate interaction
        this.log(LOG_LEVEL.Debug, fname, `CVP Conference detected. ${callState.length} calls and ${intSet.size} unique interaction Ids.`);
        conference = true;
      }

      this.log(LOG_LEVEL.Critical, fname, `${conference ? 'Currently in a Conference' : 'Not Currently in a Conference'}`);
      return conference;
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to determine if the current state is a conference. Assuming false.', error);
      return conference;
    }
  }

  private isWarmTransfer(data: IEndCallPayload): boolean {
    const fname = 'isWarmTransfer';
    let warmTransfer = false;
    try {
      const partiesInt: string[] = this.activeParties.map(ap => ap.interactionId); // full list of interactionIds
      const intSet: Set<string> = new Set(partiesInt); // unique interactionIds

      if (partiesInt.length > 2 && intSet.size < partiesInt.length) {
        // if there are at least 2 parties on the same interaction this is not a warm Transfer
        return false;
      }

      const callState: boolean[] = this.activeCalls.map(ac => ac.callAttributes.isOnHold); // full list of held statuses
      const callSet: Set<boolean> = new Set(callState); // unique held statuses

      if (callState.length > 1 && callSet.size === 1) {
        // if there are at least 2 calls and all calls are in the same state
        return false;
      }

      // original logic
      warmTransfer = data.call.callAttributes?.participantType === Constants.PARTICIPANT_TYPE.AGENT && intSet.size > 1;

      return warmTransfer;
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to determine if this is a conference or warm transfer.', error);
      return warmTransfer;
    }
  }

  private getCall(callId: string): AmcCall | undefined {
    return this.activeCalls.find(ac => ac.callId === callId);
  }

  // ToDo: Look into removing this function
  private async newUnsolicitedParticipant(call: PhoneCall, shouldAwait = false): Promise<void> {
    const initialCallHasEnded =
      !this.activeCalls.some(ac => ac.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER) ||
      this.activeCalls.some(ac => ac.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER && ac.state === Constants.CALL_STATE.ENDED);

    let awaiter: Promise<void>;

    awaiter = this.publishEventToScv({
      eventType: Constants.VOICE_EVENT_TYPE.PARTICIPANT_ADDED,
      dataType: 'ParticipantResult',
      data: new ParticipantResult({
        initialCallHasEnded,
        callId: call.callId,
        callInfo: call.callInfo,
        phoneNumber: call.phoneNumber
      })
    });

    if (shouldAwait) {
      await awaiter;
    }

    call.state = Constants.CALL_STATE.CONNECTED;

    // Raise target phone call as unsolicited event to force update
    awaiter = this.publishEventToScv({
      eventType: Constants.VOICE_EVENT_TYPE.PARTICIPANT_CONNECTED,
      dataType: 'ParticipantResult',
      data: new ParticipantResult({
        initialCallHasEnded,
        callInfo: call.callInfo,
        phoneNumber: call.phoneNumber,
        callId: call.callId
      })
    });

    if (shouldAwait) {
      await awaiter;
    }
    return;
  }

  interactionIsValid(interaction: IInteraction): interaction is Ensure<IInteraction, 'interactionId' | 'scenarioId' | 'state'> {
    // ToDo: We should check for the CAD fields of DialedPhone, Phone, and whatever the configured VendorCallKey field is here as well.
    return Boolean(interaction.interactionId &&
      interaction.scenarioId &&
      interaction.state != null);
  }

  getPartyIndex(party?: string): number {
    return this.activeParties.findIndex(ap => ap.phoneNumber === party || ap.name === party || ap.partyId === party);
  }

  getCallIndexUsingCallId(callId: string): number {
    return this.activeCalls.findIndex(ac => ac.callId === callId);
  }

  normalControls(interaction: Ensure<IInteraction, 'state' | 'interactionId' | 'scenarioId'>): CallInfo {
    const fname = this.cname + `normalControls(${interaction.interactionId}): `;
    try {
      return new CallInfo({
        isOnHold: interaction.state === INTERACTION_STATES.OnHold,
        isRecordingPaused: false,
        isMuted: false,
        initialCallId: interaction.interactionId,
        callStateTimestamp: new Date(),
        queueName: undefined,
        queueId: undefined,
        queueTimestamp: undefined,
        isSoftphoneCall: false,
        acceptEnabled: true,
        declineEnabled: this.scs.rejectEnabled ?? true,
        muteEnabled: this.scs.muteEnabled ?? true,
        swapEnabled: this.scs.swapEnabled ?? true,
        conferenceEnabled: true,
        holdEnabled: this.scs.holdEnabled ?? true,
        recordEnabled: false,
        addCallerEnabled: true,
        extensionEnabled: true,
        isReplayable: true,
        isBargeable: false,
        isExternalTransfer: false,
        showMuteButton: true,
        showRecordButton: false,
        showAddCallerButton: this.scs.addCallerEnabled ?? true,
        showAddBlindTransferButton: this.scs.blindTransferEnabled ?? true,
        showMergeButton: this.scs.conferenceEnabled ?? true,
        showSwapButton: this.scs.swapEnabled ?? true,
        removeParticipantVariant: this.scs.removeParticipantVariant ?? Constants.REMOVE_PARTICIPANT_VARIANT.ALWAYS_EXCEPT_ON_HOLD,
        additionalFields: undefined,
        isMultiParty: Boolean(interaction.multiPartyState)
      });
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Error creating normal controls, defaulting to all controls enabled.', { interaction, error });
      return new CallInfo({
        isOnHold: interaction.state === INTERACTION_STATES.OnHold,
        isRecordingPaused: false,
        isMuted: false,
        initialCallId: interaction.interactionId,
        callStateTimestamp: new Date(),
        queueName: undefined,
        queueId: undefined,
        queueTimestamp: undefined,
        isSoftphoneCall: false,
        acceptEnabled: true,
        declineEnabled: true,
        muteEnabled: true,
        swapEnabled: true,
        conferenceEnabled: true,
        holdEnabled: true,
        recordEnabled: false,
        addCallerEnabled: true,
        extensionEnabled: true,
        isReplayable: true,
        isBargeable: false,
        isExternalTransfer: false,
        showMuteButton: true,
        showRecordButton: false,
        showAddCallerButton: true,
        showAddBlindTransferButton: true,
        showMergeButton: true,
        showSwapButton: true,
        removeParticipantVariant: Constants.REMOVE_PARTICIPANT_VARIANT.ALWAYS_EXCEPT_ON_HOLD,
        additionalFields: undefined,
        isMultiParty: Boolean(interaction.multiPartyState)
      });
    }
  }

  updateCallControlsBasedOnState(interaction: IInteraction, cId: CID) {
    const fname = this.cname + `updateCallControlsBasedOnState(${cId}): `;
    try {
      if (this.activeCalls.length === 1) {
        // Enable merge if there is only one active call
        this.log(LOG_LEVEL.Trace, fname, 'There are less than 2 active calls. Setting showMergeButton to true for the first call.');
        this.activeCalls[0].callInfo.showMergeButton = this.scs.conferenceEnabled ?? true; // If the config isn't present assume that conference is enabled
        this.activeCalls[0].callInfo.removeParticipantVariant = this.scs.removeParticipantVariant ?? Constants.REMOVE_PARTICIPANT_VARIANT.ALWAYS;
      } else if (this.activeCalls.length > 1) {
        this.log(LOG_LEVEL.Trace, fname, `There are ${this.activeCalls.length} calls, checking if in a conference`);

        const conference = this.isInConference() || (interaction.multiPartyState && interaction.details?.fields['conferenceParties'].Value);

        if (conference) {
          // Disable merge if there are 2 active calls and the interaction is a conference
          this.log(LOG_LEVEL.Trace, fname, 'Interaction is a conference with 2 active calls. Disabling merge button for both calls.');
          this.activeCalls.forEach(ac => {
            ac.callInfo.showMergeButton = false;
            if (this.scs.conferenceDropParty) {
              ac.callInfo.removeParticipantVariant = this.scs.removeParticipantVariant ?? Constants.REMOVE_PARTICIPANT_VARIANT.ALWAYS;
            } else {
              this.log(LOG_LEVEL.Trace, fname, 'Disabling remove participant while in a conference.');
              ac.callInfo.removeParticipantVariant = Constants.REMOVE_PARTICIPANT_VARIANT.NEVER;
            }
          });
        } else {
          this.log(LOG_LEVEL.Trace, fname, 'Interaction is not a conference. Enabling merge button and resetting remove participant variant.');
          this.activeCalls.forEach(ac => {
            ac.callInfo.showMergeButton = true;
            ac.callInfo.removeParticipantVariant = this.scs.removeParticipantVariant ?? Constants.REMOVE_PARTICIPANT_VARIANT.ALWAYS;
          });
        }
      } else {
        this.log(LOG_LEVEL.Trace, fname, 'No changes needed to call controls');
      }
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Error updating call controls based on state', error);
    }
  }

  allControlsDisabled(originalInfo: CallInfo): CallInfo {
    return new CallInfo({
      isOnHold: originalInfo.isOnHold,
      isRecordingPaused: originalInfo.isRecordingPaused,
      isMuted: originalInfo.isMuted,
      initialCallId: originalInfo.initialCallId,
      callStateTimestamp: originalInfo.callStateTimestamp,
      queueName: originalInfo.queueName,
      queueId: originalInfo.queueId,
      queueTimestamp: originalInfo.queueTimestamp,
      isSoftphoneCall: originalInfo.isSoftphoneCall,
      acceptEnabled: false,
      declineEnabled: false,
      muteEnabled: false,
      swapEnabled: false,
      conferenceEnabled: false,
      holdEnabled: false,
      recordEnabled: false,
      addCallerEnabled: false,
      extensionEnabled: false,
      isReplayable: true,
      isBargeable: false,
      isExternalTransfer: false,
      showMuteButton: false,
      showRecordButton: false,
      showAddCallerButton: false,
      showAddBlindTransferButton: false,
      showMergeButton: false,
      showSwapButton: false,
      removeParticipantVariant: Constants.REMOVE_PARTICIPANT_VARIANT.NEVER,
      additionalFields: originalInfo.additionalFields,
      isMultiParty: originalInfo.isMultiParty
    });
  }

  sortPartiesAndCalls(interaction: IInteraction, cId?: CID): void {
    const fname = `${this.cname}sortPartiesAndCalls ${cId ? `(${cId})` : ''}: `;
    try {
      this.log(LOG_LEVEL.Debug, fname, `Before sort for interactionId: ${interaction.interactionId}`, { interaction: interaction, activeCalls: this.activeCalls, activeParties: this.activeParties });

      // Pair parties with their corresponding calls
      const paired = this.activeParties.map((party, index) => ({
        party,
        call: this.activeCalls[index]
      }));

      // Group by interactionId, preserving order of first occurrence
      const result: typeof paired = [];
      const processedInteractionIds = new Set<string>();

      for (let i = 0; i < paired.length; i++) {
        const currentInteractionId = paired[i].party.interactionId;

        if (!processedInteractionIds.has(currentInteractionId)) {
          processedInteractionIds.add(currentInteractionId);

          // Add this item and all subsequent items with the same interactionId
          for (let j = i; j < paired.length; j++) {
            if (paired[j].party.interactionId === currentInteractionId) {
              result.push(paired[j]);
            }
          }
        }
      }

      // Reassign the sorted arrays
      this.activeParties = result.map(p => p.party);
      this.activeCalls = result.map(p => p.call);

      this.log(LOG_LEVEL.Debug, fname, 'After sort', { activeCalls: this.activeCalls, activeParties: this.activeParties });

    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to sort parties / calls', error);
    }
  }

  determineEventsAndUpdateCalls(interaction: Ensure<IInteraction, 'interactionId' | 'scenarioId' | 'state'>, cId: CID): ScvVoiceEventPayload[] {
    const fname = this.cname + `determineEventsAndUpdateCalls${cId ? `(${cId}): ` : ''}`;

    try {
      this.log(LOG_LEVEL.Debug, fname, 'START >>>');

      const events: ScvVoiceEventPayload[] = [];

      const parties = this.extractPartyIds(interaction);

      partyLoop: for (const p of parties) {
        const partyIdx = this.getPartyIndex(p);
        if (partyIdx < 0) {
          this.log(LOG_LEVEL.Error, fname, `Could not find active party for ${p}`);
          continue;
        }
        if (this.activeCalls.length <= partyIdx) {
          this.log(LOG_LEVEL.Error, fname, `Could not find active call for party ${p}`);
          continue;
        }

        const party = this.activeParties[partyIdx];
        const call = this.activeCalls[partyIdx];
        const CALL_STATES = Constants.CALL_STATE;
        const EVENTS = Constants.VOICE_EVENT_TYPE;

        this.log(LOG_LEVEL.Loop, fname, `Handling party ${p} at index ${partyIdx}.`, { party, call });

        if (partyIdx > 1) { // 0 and 1 are the only parties that should be rendered
          this.log(LOG_LEVEL.Warning, fname, `Party index is ${partyIdx} for ${p}. Not raising events`);
          if (interaction.state === INTERACTION_STATES.Disconnected) {
            party.removalDetails.isRemoved = true;
            party.removalDetails.preserveOtherCalls = true;
            this.log(LOG_LEVEL.Debug, fname, `Silently removing a ${party}`);
          } else if (
            (interaction.state === INTERACTION_STATES.Alerting || interaction.state === INTERACTION_STATES.Connected) &&
            this.activeCalls.length > 2 &&
            this.activeParties.length > 2
          ) {
            const initialCallHasEnded =
              !this.activeCalls.some(ac => ac.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER) ||
              this.activeCalls.some(ac => ac.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER && ac.state === Constants.CALL_STATE.ENDED);

            // callInfo and callId must be corresponding to call 2 otherwise the Salesforce UI will break
            this.pushEvent(cId, events, deepCopy({
              eventType: Constants.VOICE_EVENT_TYPE.PARTICIPANT_CONNECTED,
              dataType: 'ParticipantResult',
              data: new ParticipantResult({
                initialCallHasEnded,
                callInfo: this.activeCalls[1].callInfo,
                phoneNumber: this.activeCalls[partyIdx].phoneNumber,
                callId: this.activeCalls[1].callId
              })
            }));
            this.activeCalls[partyIdx].alreadyRaisedToSalesforce = true;
            this.log(LOG_LEVEL.Debug, fname, `Overwriting displayed phone number for the second call with last call in activeCalls phone number. ${this.activeCalls[this.activeCalls.length - 1].phoneNumber}`);
          }
          continue;
        }

        this.log(LOG_LEVEL.Information, fname, `Interaction event: [${INTERACTION_STATES[interaction.state]}]`, { call, party });

        // handle all possible cases for all interaction states
        switch (interaction.state) {
        // Alerting
        case INTERACTION_STATES.Alerting: {
          if (this.activeCalls[partyIdx].callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER) {
            // Initial Caller alerting event
            call.state = CALL_STATES.RINGING;
            if (call.alreadyRaisedToSalesforce === true) {
              this.log(LOG_LEVEL.Critical, fname, `Already raised call started event for ${call.callId}`);
            } else {
              this.log(LOG_LEVEL.Critical, fname, `Initial Caller alerting event: ${call.callId}`, { call, interaction });
              this.pushEvent(cId, events, deepCopy({
                eventType: EVENTS.CALL_STARTED,
                dataType: 'CallResult',
                data: new CallResult({
                  call
                })
              }));
            }
          } else {
            // call.state = CALL_STATES.RINGING;
            // this.newUnsolicitedParticipant(call, false);

            const initialCallHasEnded =
              !this.activeCalls.some(ac => ac.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER) ||
              this.activeCalls.some(ac => ac.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER && ac.state === Constants.CALL_STATE.ENDED);

            this.pushEvent(cId, events, deepCopy({
              eventType: Constants.VOICE_EVENT_TYPE.PARTICIPANT_ADDED,
              dataType: 'ParticipantResult',
              data: new ParticipantResult({
                initialCallHasEnded,
                callId: call.callId,
                callInfo: call.callInfo,
                phoneNumber: call.phoneNumber
              })
            }));

            call.state = Constants.CALL_STATE.CONNECTED;

            // Raise target phone call as unsolicited event to force update
            this.pushEvent(cId, events, deepCopy({
              eventType: Constants.VOICE_EVENT_TYPE.PARTICIPANT_CONNECTED,
              dataType: 'ParticipantResult',
              data: new ParticipantResult({
                initialCallHasEnded,
                callInfo: call.callInfo,
                phoneNumber: call.phoneNumber,
                callId: call.callId
              })
            }));
          }
          call.alreadyRaisedToSalesforce = true;
        }
          break;
          // Initated
        case INTERACTION_STATES.Initiated: {
          call.state = this.scs.outboundAutoConnect ? CALL_STATES.CONNECTED : CALL_STATES.RINGING;
          if (this.activeCalls[partyIdx].callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER) {
            this.pushEvent(cId, events, deepCopy({
              eventType: EVENTS.CALL_STARTED,
              dataType: 'CallResult',
              data: new CallResult({
                call
              })
            }));
            // Send immediate connected event
            if (this.scs.outboundAutoConnect) {
              this.pushEvent(cId, events, deepCopy({
                eventType: EVENTS.CALL_CONNECTED,
                dataType: 'CallResult',
                data: new CallResult({
                  call
                })
              }));
            }
          } else {
            this.pushEvent(cId, events, deepCopy({
              eventType: EVENTS.PARTICIPANT_ADDED,
              dataType: 'ParticipantResult',
              data: new ParticipantResult({
                callId: call.callId,
                callInfo: call.callInfo,
                initialCallHasEnded: false, // Initial call can never end,
                phoneNumber: party.phoneNumber
              })
            }));

            if (this.scs.outboundAutoConnect) {
              // Send immediate connected event
              this.pushEvent(cId, events, deepCopy({
                eventType: EVENTS.PARTICIPANT_CONNECTED,
                dataType: 'ParticipantResult',
                data: new ParticipantResult({
                  callId: call.callId,
                  callInfo: call.callInfo,
                  initialCallHasEnded: false, // Initial call can never end,
                  phoneNumber: party.phoneNumber
                })
              }));
            }

            // Resend first CALL_CONNECTED event to force Salesforce to update itself with the
            // proper state of each call (in case this agent was just the recipient of a call merge,
            // salesforce seems to assume that the first call is held when only a PARTY_ADD occurs)
            for (let otherPartyIdx = 0; otherPartyIdx < this.activeParties.length; otherPartyIdx++) {
              if (otherPartyIdx === partyIdx) {
                continue;
              }
              if (otherPartyIdx >= this.activeCalls.length) {
                this.log(LOG_LEVEL.Error, fname, 'Could not find active call when updating existing calls on merge receive');
                continue;
              }

              if (this.activeCalls[otherPartyIdx].callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER) {
                this.pushEvent(cId, events, deepCopy({
                  eventType: EVENTS.CALL_CONNECTED,
                  dataType: 'CallResult',
                  data: new CallResult({
                    call: this.activeCalls[otherPartyIdx]
                  })
                }));
              } else {
                this.pushEvent(cId, events, deepCopy({
                  eventType: EVENTS.PARTICIPANT_CONNECTED,
                  dataType: 'ParticipantResult',
                  data: new ParticipantResult({
                    callId: this.activeCalls[otherPartyIdx].callId,
                    callInfo: this.activeCalls[otherPartyIdx].callInfo,
                    initialCallHasEnded: this.activeCalls.some(ac => ac.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER && ac.state !== CALL_STATES.ENDED),
                    phoneNumber: this.activeCalls[otherPartyIdx].phoneNumber
                  })
                }));
              }
            }
          }
        }
          call.alreadyRaisedToSalesforce = true;
          break;
          // Connected
        case INTERACTION_STATES.Connected: {
          // Duplicate events for this state are allowed
          call.state = CALL_STATES.CONNECTED;

          // Could be a HANGUP, and a party has been removed
          if (
            this.activeParties.some(ap =>
              ap.interactionId === interaction.interactionId &&
              ap.scenarioId === interaction.scenarioId &&
              !parties.includes(ap.phoneNumber) &&
              !parties.includes(ap.name) &&
              !parties.includes(ap.partyId)
            )
          ) {
            this.removePartiesNotOnInteraction(interaction, events, parties, cId);
          // Could be a hold toggle
          } else if (call.callAttributes.isOnHold) {
            call.callAttributes.isOnHold = false;
            call.callInfo.isOnHold = false;
            this.pushEvent(cId, events, deepCopy({
              eventType: EVENTS.HOLD_TOGGLE,
              dataType: 'HoldToggleResult',
              data: new HoldToggleResult({
                isCustomerOnHold: this.activeCalls[0].callAttributes.isOnHold,
                isThirdPartyOnHold: this.activeCalls.slice(1).some(ac => ac.callAttributes.isOnHold),
                // calls: this.activeCalls
              })
            }));
          } else if (this.activeCalls[partyIdx].callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER) {
            if (!call.alreadyRaisedToSalesforce) {
              this.pushEvent(cId, events, deepCopy({
                eventType: EVENTS.CALL_STARTED,
                dataType: 'CallResult',
                data: new CallResult({
                  call
                })
              }));
            }
            // Initial Caller
            this.pushEvent(cId, events, deepCopy({
              eventType: EVENTS.CALL_CONNECTED,
              dataType: 'CallResult',
              data: new CallResult({
                call
              })
            }));
          } else {
            if (!call.alreadyRaisedToSalesforce) {
              this.pushEvent(cId, events, deepCopy({
                eventType: EVENTS.PARTICIPANT_ADDED,
                dataType: 'ParticipantResult',
                data: new ParticipantResult({
                  callId: call.callId,
                  callInfo: call.callInfo,
                  initialCallHasEnded: false, // Initial call can never end,
                  phoneNumber: party.phoneNumber
                })
              }));
            }
            this.pushEvent(cId, events, deepCopy({
              eventType: EVENTS.PARTICIPANT_CONNECTED,
              dataType: 'ParticipantResult',
              data: new ParticipantResult({
                callId: call.callId,
                callInfo: call.callInfo,
                initialCallHasEnded: false, // Initial call can never end,
                phoneNumber: party.phoneNumber
              })
            }));

            // Resend first CALL_CONNECTED event to force Salesforce to update itself with the
            // proper state of each call (in case this agent was just the recipient of a call merge,
            // salesforce seems to assume that the first call is held when only a PARTY_ADD occurs)
            for (let otherPartyIdx = 0; otherPartyIdx < this.activeParties.length; otherPartyIdx++) {
              if (otherPartyIdx === partyIdx) {
                continue;
              }
              if (otherPartyIdx >= this.activeCalls.length) {
                this.log(LOG_LEVEL.Error, fname, 'Could not find active call when updating existing calls on merge receive');
                continue;
              }

              if (this.activeCalls[otherPartyIdx].callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER) {
                this.pushEvent(cId, events, deepCopy({
                  eventType: EVENTS.CALL_CONNECTED,
                  dataType: 'CallResult',
                  data: new CallResult({
                    call: this.activeCalls[otherPartyIdx]
                  })
                }));
              } else {
                this.pushEvent(cId, events, deepCopy({
                  eventType: EVENTS.PARTICIPANT_CONNECTED,
                  dataType: 'ParticipantResult',
                  data: new ParticipantResult({
                    callId: this.activeCalls[otherPartyIdx].callId,
                    callInfo: this.activeCalls[otherPartyIdx].callInfo,
                    initialCallHasEnded: this.activeCalls.some(ac => ac.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER && ac.state !== CALL_STATES.ENDED),
                    phoneNumber: this.activeCalls[otherPartyIdx].phoneNumber
                  })
                }));
              }

              // We only want to raise one of the two events due to Salesforce's limitation on the number of calls displayed
              break;
            }
          }
          call.alreadyRaisedToSalesforce = true;
        }
          break;
          // OnHold
        case INTERACTION_STATES.OnHold: {
          call.state = CALL_STATES.CONNECTED;
          // Could be a HANGUP, and a party has been removed
          if (
            this.activeParties.some(ap =>
              ap.interactionId === interaction.interactionId &&
              ap.scenarioId === interaction.scenarioId &&
              !parties.includes(ap.phoneNumber) &&
              !parties.includes(ap.name) &&
              !parties.includes(ap.partyId)
            )
          ) {
            this.removePartiesNotOnInteraction(interaction, events, parties, cId);
          // Could be a hold toggle
          } else if (!call.callAttributes.isOnHold) {
            call.callAttributes.isOnHold = true;
            call.callInfo.isOnHold = true;

            if (call.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER) {
              this.pushEvent(cId, events, deepCopy({
                eventType: EVENTS.CALL_CONNECTED,
                dataType: 'CallResult',
                data: new CallResult({
                  call
                })
              }));
            } else if (call.callAttributes.participantType === Constants.PARTICIPANT_TYPE.THIRD_PARTY) {
              this.pushEvent(cId, events, deepCopy({
                eventType: EVENTS.PARTICIPANT_CONNECTED,
                dataType: 'ParticipantResult',
                data: new ParticipantResult({
                  callId: call.callId,
                  callInfo: call.callInfo,
                  initialCallHasEnded: false, // Initial call can never end,
                  phoneNumber: party.phoneNumber
                })
              }));
            }

            if (call.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER) {
              this.pushEvent(cId, events, deepCopy({
                eventType: EVENTS.HOLD_TOGGLE,
                dataType: 'HoldToggleResult',
                data: new HoldToggleResult({
                  isCustomerOnHold: call ? call.callAttributes.isOnHold : false,
                  isThirdPartyOnHold: this.activeCalls.some(ac =>
                    ac.callAttributes.participantType !== Constants.PARTICIPANT_TYPE.INITIAL_CALLER &&
                      ac.callAttributes.isOnHold
                  ),
                  calls: [call]
                })
              }));
            } else {
              this.pushEvent(cId, events, deepCopy({
                eventType: EVENTS.HOLD_TOGGLE,
                dataType: 'HoldToggleResult',
                data: new HoldToggleResult({
                  isCustomerOnHold: this.activeCalls.some(ac =>
                    ac.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER &&
                      ac.callAttributes.isOnHold
                  ),
                  isThirdPartyOnHold: call ? call.callAttributes.isOnHold : false,
                  calls: [call]
                })
              }));
            }

          }
        }
          break;
          // Disonnected
        case INTERACTION_STATES.Disconnected: {
          party.removalDetails.isRemoved = true;

          if (
            this.promisesToResolve.some(p2r => p2r.pending && p2r.kind === 'BlindTransfer') &&
            this.activeParties.length > 1 &&
            this.activeCalls.length > 1 &&
            call.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER &&
            this.activeParties.filter(p => p.interactionId === interaction.interactionId).length === 1
          ) {
            this.log(LOG_LEVEL.Trace, fname, `Pending blind transfer, detected disconnect event for ${party.phoneNumber}, and that party is the initial caller`);
            // ! this should only happen in a scenario where DPG raised the initiated event for the transfer to agent2
            party.removalDetails.preserveOtherCalls = true;
            call.state = CALL_STATES.ENDED;
            this.pushEvent(cId, events, deepCopy({
              eventType: EVENTS.HANGUP,
              dataType: 'HangupResult',
              data: new HangupResult({
                calls: [call]
              })
            }));
          } else if (
            this.promisesToResolve.filter(p2r => p2r.pending && p2r.kind === 'Hangup').length > 1 && // If there are 2 pending hangups it is a warm transfer
            this.activeParties.length > 1 &&
            this.activeCalls.length > 1 &&
            call.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER &&
            this.activeParties.filter(p => p.interactionId === interaction.interactionId).length === 1
          ) {
            this.log(LOG_LEVEL.Trace, fname, `Pending hangups, detected warm transfer for ${party.phoneNumber}, and that party is the initial caller`);
            // ! this should only happen in a scenario where DPG raised the initiated event for the transfer to agent2
            party.removalDetails.preserveOtherCalls = false;
            call.state = CALL_STATES.ENDED;
            this.pushEvent(cId, events, deepCopy({
              eventType: EVENTS.HANGUP,
              dataType: 'HangupResult',
              data: new HangupResult({
                calls: [call]
              })
            }));

          } else if (!this.promisesToResolve.some(p2r => p2r.pending && p2r.kind === 'Hangup') && this.activeParties.length > 1) {
            this.log(LOG_LEVEL.Trace, fname, `Unsolicited Disconnect for ${interaction.interactionId} for party ${party.partyId}`, parties);

            if (
              parties.length > 1 && // ! The interaction corresponds to multiple parties
              this.activeParties.length > 1 &&
              this.activeCalls.length > 1
            ) {
              // ! Dropping both parties for a multiparty scenario (Conference only)
              this.log(LOG_LEVEL.Critical, fname, `Multiple parties to remove for disconnected interaction ${interaction.interactionId}`, parties);

              if (party.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER) {
                this.log(LOG_LEVEL.Debug, fname, 'Party is initial caller', party);
                const thirdParty = this.activeParties.find(tp => tp.participantType !== Constants.PARTICIPANT_TYPE.INITIAL_CALLER);
                if (!thirdParty) {
                  throw new Error('There is no thirdParty despite there being > 2 activeParties on a disconnect event.');
                }
                const thirdCall = this.activeCalls.find(tc => tc.callAttributes.participantType !== Constants.PARTICIPANT_TYPE.INITIAL_CALLER);
                if (!thirdCall) {
                  throw new Error('There is not thirdCall despite there being > 2 activeCalls on a disconnect event.');
                }

                thirdParty.removalDetails.isRemoved = true;
                thirdParty.removalDetails.preserveOtherCalls = false;
                this.pushEvent(cId, events, deepCopy({
                  eventType: EVENTS.PARTICIPANT_REMOVED,
                  dataType: 'CallResult',
                  data: new CallResult({
                    call: thirdCall
                  })
                }));

                party.removalDetails.isRemoved = true;
                party.removalDetails.preserveOtherCalls = false;
                call.state = CALL_STATES.ENDED;
                this.pushEvent(cId, events, deepCopy({
                  eventType: EVENTS.HANGUP,
                  dataType: 'HangupResult',
                  data: new HangupResult({
                    calls: [call]
                  })
                }));
              } else {
                this.log(LOG_LEVEL.Debug, fname, 'Party is not initial caller', party);
                const initialParty = this.activeParties.find(tp => tp.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER);
                if (!initialParty) {
                  throw new Error('There is no initialParty despite there being > 2 activeParties on a disconnect event.');
                }
                const initialCall = this.activeCalls.find(tc => tc.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER);
                if (!initialCall) {
                  throw new Error('There is no initialCall despite there being > 2 activeCalls on a disconnect event.');
                }

                party.removalDetails.isRemoved = true;
                party.removalDetails.preserveOtherCalls = false;
                this.pushEvent(cId, events, deepCopy({
                  eventType: EVENTS.PARTICIPANT_REMOVED,
                  dataType: 'CallResult',
                  data: new CallResult({
                    call
                  })
                }));

                initialParty.removalDetails.isRemoved = true;
                initialParty.removalDetails.preserveOtherCalls = false;
                initialCall.state = CALL_STATES.ENDED;
                this.pushEvent(cId, events, deepCopy({
                  eventType: EVENTS.HANGUP,
                  dataType: 'HangupResult',
                  data: new HangupResult({
                    calls: [initialCall]
                  })
                }));
              }

              break partyLoop;
            } else {
              // ! dropping a single party from a multiparty scenario (Conference and Consult)
              const previousCallInfo = call.callInfo;
              // call.callInfo = this.allControlsDisabled(call.callInfo);
              party.removalDetails.isRemoved = false;
              /* Initial Caller, this is among the most complicated behaviors for SCV.
                Ideally, this call should never go away. If this is truly an unsolicited
                event, then we can trick SF to think that the *other* call was removed,
                but the initial caller remains (with updated contact information).

                If it is *not* a solicited event, however, and the Initial_caller call
                was removed directly within the Omni-Widget, then SF will expect that this
                call be removed, and we must allow it.

                Therefore, if this is a removal of the Initial caller, and there is more
                than one call, we will generate events that tell Salesforce that a different
                call (the last call in the active calls list) has gone away.

                If there is only one active call, or this is a solicited event, then we must
                tell Salesforce that the call it expects has been ended with a normal HangupResult.
                */

              // Unsolicited, preserve Initial_Caller
              if (this.activeCalls[partyIdx].callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER) {
                this.log(LOG_LEVEL.Trace, fname, `Detected unsolicited disconnect for ${party.phoneNumber}, removing initial caller`);
                party.removalDetails.preserveOtherCalls = true;
                // Tell salesforce the last call in the list has been removed,
                // as all calls will be shifted up to match the active parties list
                let lastCall = this.activeCalls[this.activeCalls.length - 1];
                let lastParty = this.activeParties[this.activeParties.length - 1];

                if (this.activeCalls.length > 2) {
                  // Salesforce's limitation on the number of displayed calls prevents us from using the last call in the list if the size is greater than 2
                  this.log(LOG_LEVEL.Trace, fname, `${this.activeCalls.length} calls, using the second call as the initial call instead of the last call`);
                  lastCall = this.activeCalls[1];
                  lastParty = this.activeParties[1];
                }

                lastParty.removalDetails.isRemoved = true;
                lastParty.removalDetails.preserveOtherCalls = true;

                // ! Update the initial call and party with the information from the remaining call and party
                this.updateContact(cId, partyIdx, lastCall.phoneNumber, lastParty.interactionId, lastParty.scenarioId, lastCall.callAttributes.isOnHold);

                this.pushEvent(cId, events, deepCopy({
                  eventType: EVENTS.CALL_CONNECTED,
                  dataType: 'CallResult',
                  data: new CallResult({
                    call
                  })
                }));

                this.pushEvent(cId, events, deepCopy({
                  eventType: EVENTS.PARTICIPANT_REMOVED,
                  dataType: 'CallResult',
                  data: new CallResult({
                    call: lastCall
                  })
                }));

                if (this.activeCalls.length > 2) {
                  this.log(LOG_LEVEL.Debug, fname, `${this.activeCalls.length} calls, adding last call after drop.`);

                  const thirdCall = this.activeCalls[this.activeCalls.length - 1];
                  const thirdParty = this.activeParties[this.activeParties.length - 1];

                  this.log(LOG_LEVEL.Trace, fname, `Displaying ${thirdParty.name}`, {thirdCall, thirdParty});

                  this.pushEvent(cId, events, deepCopy({
                    eventType: EVENTS.PARTICIPANT_ADDED,
                    dataType: 'ParticipantResult',
                    data: new ParticipantResult({
                      callId: thirdCall.callId,
                      callInfo: thirdCall.callInfo,
                      initialCallHasEnded: false, // Initial call can never end,
                      phoneNumber: thirdParty.phoneNumber
                    })
                  }));

                  this.pushEvent(cId, events, deepCopy({
                    eventType: EVENTS.PARTICIPANT_CONNECTED,
                    dataType: 'ParticipantResult',
                    data: new ParticipantResult({
                      initialCallHasEnded: false,
                      phoneNumber: thirdParty.phoneNumber,
                      callId: thirdCall.callId,
                      callInfo: thirdCall.callInfo
                    })
                  }));
                }
              } else {
                this.log(LOG_LEVEL.Trace, fname, `Detected unsolicited disconnect for ${party.phoneNumber}, removing third party`);

                party.removalDetails.isRemoved = true;
                party.removalDetails.preserveOtherCalls = true;

                this.pushEvent(cId, events, deepCopy({
                  eventType: EVENTS.PARTICIPANT_CONNECTED,
                  dataType: 'ParticipantResult',
                  data: new ParticipantResult({
                    initialCallHasEnded: false,
                    phoneNumber: party.phoneNumber,
                    callId: call.callId,
                    callInfo: call.callInfo
                  })
                }));

                this.pushEvent(cId, events, deepCopy({
                  eventType: EVENTS.PARTICIPANT_REMOVED,
                  dataType: 'CallResult',
                  data: new CallResult({
                    call
                  })
                }));

              }
            }
          } else if (
            this.promisesToResolve.some(p2r => p2r.pending && p2r.kind === 'Hangup') &&
            this.activeParties.length > 1 &&
            this.activeCalls.length > 1 &&
            call.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER &&
            this.activeParties.filter(p => p.interactionId === interaction.interactionId).length === 1
          ) {
            // ! Solicited drop initial caller
            this.log(LOG_LEVEL.Trace, fname, `Pending hangup, detected disconnect event for ${party.phoneNumber}, and that party is the initial caller`);
            party.removalDetails.isRemoved = false;

            const lastCall = this.activeCalls[this.activeCalls.length - 1];
            const lastParty = this.activeParties[this.activeParties.length - 1];
            lastParty.removalDetails.preserveOtherCalls = true;
            lastParty.removalDetails.isRemoved = true;

            // ! Update the remaining call with the information of the removed Third Party
            this.updateContact(cId, partyIdx, lastCall.phoneNumber, lastParty.interactionId, lastParty.scenarioId, lastCall.callAttributes.isOnHold);

            // ! update the initial call with the information from the third party
            this.pushEvent(cId, events, deepCopy({
              eventType: EVENTS.CALL_CONNECTED,
              dataType: 'CallResult',
              data: new CallResult({
                call: call
              })
            }));

            // ! Drop the third party instead of the Initial Caller
            this.pushEvent(cId, events, deepCopy({
              eventType: EVENTS.PARTICIPANT_REMOVED,
              dataType: 'CallResult',
              data: new CallResult({
                call: lastCall
              })
            }));
            break;
          } else {
            this.log(LOG_LEVEL.Information, fname, `Treating disconnect as hangup for ${party.phoneNumber}. Will not preserve other calls`);
            party.removalDetails.preserveOtherCalls = false;
            call.state = CALL_STATES.ENDED;
            this.pushEvent(cId, events, deepCopy({
              eventType: EVENTS.HANGUP,
              dataType: 'HangupResult',
              data: new HangupResult({
                calls: [call]
              })
            }));
          }
        }
          break;
        }
      }

      return events;
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);

      throw err;
    } finally {
      this.log(LOG_LEVEL.Debug, fname, 'END <<<');
    }
  }
  /**
 * Marks parties to be removed that are associated to the current interaction, but no longer present on it.
 *
 * @param {(Ensure<IInteraction, 'interactionId' | 'scenarioId' | 'state'>)} interaction The current interaction being processed
 * @param {ScvVoiceEventPayload[]} events The list of events to raise to Salesforce
 * @param {string[]} parties The parties from the current interaction
 * @param {CID} cId
 * @memberof HomeBYOTComponent
 */
  private removePartiesNotOnInteraction(interaction: Ensure<IInteraction, 'interactionId' | 'scenarioId' | 'state'>, events: ScvVoiceEventPayload[], parties: string[], cId: CID): void {
    const fname = this.cname + `removePartiesNotOnInteraction${cId ? `(${cId}): ` : ''}`;

    try {
      // there are active parties that match this interaction which aren't in extracted parties
      this.log(LOG_LEVEL.Debug, fname, 'Removing parties no longer on the interaction');
      let removedParties = this.activeParties.filter(ap =>
        ap.interactionId === interaction.interactionId &&
        ap.scenarioId === interaction.scenarioId &&
        !parties.includes(ap.phoneNumber) &&
        !parties.includes(ap.name) &&
        !parties.includes(ap.partyId)
      );

      if (removedParties.length === 1 && removedParties[0].participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER) {
        this.log(LOG_LEVEL.Critical, fname, 'Attempting to remove initial caller when dropping a party, removing third party instead and updating initial call to have third party\'s details');

        removedParties = this.activeParties.filter(ap =>
          ap.interactionId === interaction.interactionId &&
            ap.scenarioId === interaction.scenarioId &&
            parties.includes(ap.phoneNumber) &&
            parties.includes(ap.name) &&
            parties.includes(ap.partyId)
        );
      }

      this.log(LOG_LEVEL.Trace, fname, `Removing ${removedParties.length} of ${parties.length} parties.`, { parties, removedParties});

      for (const removedParty of removedParties) {
        const removedpartyIdx = this.getPartyIndex(removedParty.phoneNumber);
        if (removedpartyIdx < 0) {
          throw new Error('Detected PARTY_REMOVED on connected event, but could not find active party');
        }
        if (removedpartyIdx >= this.activeCalls.length) {
          throw new Error('Detected PARTY_REMOVED on connected event, but could not find active call');
        }

        removedParty.removalDetails.isRemoved = true;

        // This is a very complicated condition.
        // Only "preserve" other calls in the active calls list if any of the following are true:
        // 1. The party being removed is not for the "Initial Caller"
        // 2. There is no pending "hangup" promise (Salesforce isn't expecting any particular call to end)
        // 3. There IS a pending hangup promise, but the call we detected as ended does not match the call Salesforce expects

        removedParty.removalDetails.preserveOtherCalls = (
          this.activeCalls[removedpartyIdx].callAttributes.participantType !== Constants.PARTICIPANT_TYPE.INITIAL_CALLER ||
            !this.promisesToResolve.some(p2r => p2r.pending && p2r.kind === 'Hangup') ||
            (
              !this.promisesToResolve.some(p2r =>
                p2r.pending &&
                p2r.kind === 'Hangup' &&
                removedpartyIdx >= 0 &&
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                this.activeCalls[removedpartyIdx].callId !== (p2r as HangupDeferredPromise).additionalDetails.callId
              )
            )
        );
        this.activeCalls[removedpartyIdx].state = Constants.CALL_STATE.ENDED;

        this.pushEvent(cId, events, deepCopy({
          eventType: Constants.VOICE_EVENT_TYPE.PARTICIPANT_REMOVED,
          dataType: 'CallResult',
          data: new CallResult({
            call: removedParty.removalDetails.preserveOtherCalls ? this.activeCalls[this.activeCalls.length - 1] : this.activeCalls[removedpartyIdx]
          })
        }));
      }
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, `Failed to remove party for interactionId: ${interaction.interactionId}`);
    }
  }

  resolveSolicitedCommands(cId: CID, interaction: Ensure<IInteraction, 'interactionId' | 'scenarioId' | 'state'>, events: ScvVoiceEventPayload[]): ScvVoiceEventPayload[] {
    const fname = this.cname + `resolveSolicitedCommands${cId ? `(${cId}): ` : ''}`;

    try {
      this.log(LOG_LEVEL.Debug, fname, `${this.promisesToResolve.length} promises to check against ${this.activeCalls.length} calls and ${this.activeParties.length} parties.`, { interaction: interaction, calls: this.activeCalls, parties: this.activeParties });
      this.log(LOG_LEVEL.Loop, fname, `${events.length} events to handle before checking.`, events);

      for (const promise of this.promisesToResolve) {
        if (!promise.pending) {
          continue;
        }
        this.log(LOG_LEVEL.Loop, fname, `Resolving promise ${promise.kind}`, promise);

        switch (promise.kind) {
        case 'AcceptReject': {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const p = promise as AcceptRejectDeferredPromise;
          if (p.isAccept) {
            events = events.filter(ev => {
              if (p.pending && ev.dataType === 'CallResult') {
                this.log(LOG_LEVEL.Information, fname, `Resolving ${p.kind}`, ev);
                p.resolve?.(ev.data);
                return false;
              }
              return true;
            });
          } else {
            events = events.filter(ev => {
              if (p.pending && ev.dataType === 'HangupResult') {
                const satCall = saturatePhoneCall(ev.data.calls[0]).saturated;
                this.log(LOG_LEVEL.Information, fname, `Resolving ${p.kind}`, new CallResult({
                  call: satCall
                }));
                p.resolve?.(new CallResult({
                  call: satCall
                }));
                return false;
              }
              return true;
            });
          }
        }
          break;
        case 'AgentStatus':
          // Do nothing
          break;
        case 'Logout':
          // Do nothing
          break;
        case 'Hangup': {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const p = promise as HangupDeferredPromise;
          events = events.filter(ev => {
            if (p.pending && ev.dataType === 'HangupResult') {
              this.log(LOG_LEVEL.Information, fname, `Resolving ${p.kind} (${p.id})`, ev);
              p.resolve?.(ev.data);
              return false;
            }
            return true;
          });

          // If we weren't able to resolve the promise, it could be that
          // a "PARTICIPANT_REMOVED" event was detected
          if (p.pending) {
            events = events.filter(
              ev => {
                if (p.pending && ev.eventType === Constants.VOICE_EVENT_TYPE.PARTICIPANT_REMOVED && ev.dataType === 'CallResult') {
                  const satCall = saturatePhoneCall(ev.data.call).saturated;
                  this.log(LOG_LEVEL.Information, fname, `Resolving ${p.kind}`, new HangupResult({
                    calls: [satCall]
                  }));
                  p.resolve?.(new HangupResult({
                    calls: [satCall]
                  }));
                  return false;
                }
                return true;
              }
            );
          }
        }
          break;
        case 'Mute':
          events = events.filter(ev => {
            if (promise.pending && ev.dataType === 'MuteToggleResult') {
              this.log(LOG_LEVEL.Information, fname, `Resolving ${promise.kind}`, ev);
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              (promise as MuteToggleDeferredPromise).resolve?.(ev.data);
              return false;
            }
            return true;
          });
          break;
        case 'Hold':
          events = events.filter(ev => {
            if (promise.pending && ev.dataType === 'HoldToggleResult') {
              this.log(LOG_LEVEL.Information, fname, `Resolving ${promise.kind}`, new HoldToggleResult({
                isCustomerOnHold: this.activeCalls[0].callAttributes.isOnHold,
                isThirdPartyOnHold: this.activeCalls.some(ac => ac.callAttributes.participantType !== Constants.PARTICIPANT_TYPE.INITIAL_CALLER && ac.callAttributes.isOnHold),
                // calls: this.activeCalls
              }));
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              (promise as HoldToggleDeferredPromise).resolve?.(new HoldToggleResult({
                isCustomerOnHold: this.activeCalls[0].callAttributes.isOnHold,
                isThirdPartyOnHold: this.activeCalls.some(ac => ac.callAttributes.participantType !== Constants.PARTICIPANT_TYPE.INITIAL_CALLER && ac.callAttributes.isOnHold),
                // calls: this.activeCalls
              }));
              return false;
            }
            return true;
          });
          break;
        case 'Participant': {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const p = promise as ParticipantDeferredPromise;

          if (
            interaction.state === INTERACTION_STATES.Connected &&
            this.activeCalls.length > 1 &&
            this.activeParties.length > 1
          ) {
            let partyAddedEvent: ScvVoiceEventPayload | undefined;
            let partyConnectedEvent: ScvVoiceEventPayload | undefined;

            let partyIndex = this.getPartyIndex(p.additionalDetails.contact.phoneNumber);
            if (partyIndex < 0) {
              this.log(LOG_LEVEL.Error, fname, 'Could not find active party, using last party', p.additionalDetails);
              partyIndex = this.activeParties.length - 1; // This is a workaround for when the dialed number doesn't match the resolving number
            }

            const activeContact = new Contact(p.additionalDetails.contact);

            // ToDo: Identify if this is necessary to add back for any reason.
            // this.activeParties[partyIndex].name = activeContact.name;
            // this.activeParties[partyIndex].partyId = activeContact.id;
            // this.activeCalls[partyIndex].contact = activeContact;

            events = events.filter(ev => {
              if (ev.dataType === 'ParticipantResult') {
                if (ev.eventType === Constants.VOICE_EVENT_TYPE.PARTICIPANT_ADDED) {
                  partyAddedEvent = ev;
                } else if (ev.eventType === Constants.VOICE_EVENT_TYPE.PARTICIPANT_CONNECTED) {
                  partyConnectedEvent = ev;
                }
                this.log(LOG_LEVEL.Information, fname, `Resolving ${promise.kind}`, ev);
                if (p.pending) {
                  p.resolve?.(ev.data);
                  return false;
                }
              }
              return true;
            });
            setTimeout(
              (addEvent?: ScvVoiceEventPayload, connectEvent?: ScvVoiceEventPayload) => {
                if (!addEvent) {
                  this.log(LOG_LEVEL.Warning, fname, 'Could not find a PARTICIPANT_ADDED event for add party', events);
                } else {
                  this.publishEventToScv(addEvent, cId);
                }

                if (!connectEvent) {
                  this.log(LOG_LEVEL.Warning, fname, 'Could not find a PARTICIPANT_CONNECTED event for add party', events);
                } else {
                  this.publishEventToScv(connectEvent, cId);
                }
              },
              0,
              partyAddedEvent,
              partyConnectedEvent
            );
          }
        }
          break;
        case 'Conference': {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const p = promise as ConferenceDeferredPromise;
          this.log(LOG_LEVEL.Trace, fname, 'Resolving conference promise for.', events);
          if (this.activeCalls.length > 1 && interaction.state !== INTERACTION_STATES.Disconnected) {
            const partyAddedIdx = events.findIndex(re => re.eventType === Constants.VOICE_EVENT_TYPE.PARTICIPANT_ADDED);
            const partyAddedEvent = events.find(re => re.eventType === Constants.VOICE_EVENT_TYPE.PARTICIPANT_ADDED);
            const partyConnectedIdx = events.findIndex(re => re.eventType === Constants.VOICE_EVENT_TYPE.PARTICIPANT_CONNECTED);
            const partyConnectedEvent = events.find(re => re.eventType === Constants.VOICE_EVENT_TYPE.PARTICIPANT_CONNECTED);

            if (partyAddedEvent) {
              events.splice(partyAddedIdx, 1);
            }
            if (partyConnectedEvent) {
              events.splice(partyConnectedIdx, 1);
            }

            if (partyAddedEvent) {
              this.publishEventToScv(partyAddedEvent, cId);
            }
            if (partyConnectedEvent) {
              this.publishEventToScv(partyConnectedEvent, cId);
            }

            const conferenceHeld = interaction.state === INTERACTION_STATES.OnHold;
            this.activeCalls[0].callAttributes.isOnHold = conferenceHeld;
            this.activeCalls[0].callInfo.isOnHold = conferenceHeld;
            this.activeCalls[1].callAttributes.isOnHold = conferenceHeld;
            this.activeCalls[1].callInfo.isOnHold = conferenceHeld;

            if (partyAddedEvent || partyConnectedEvent) {
              const holdToggle = new HoldToggleResult({
                // calls: this.activeCalls,
                isCustomerOnHold: this.activeCalls[0].callAttributes.isOnHold,
                isThirdPartyOnHold: this.activeCalls[1].callAttributes.isOnHold
              });
              this.log(LOG_LEVEL.Information, fname, `Resolving ${promise.kind}`, holdToggle);
              p.resolve?.(holdToggle);
            }
          }
        }
          break;
        case 'OutboundDial': {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const p = promise as OutboundDialDeferredPromise;
          const callStartedEventIdx = events.findIndex(re => re.eventType === Constants.VOICE_EVENT_TYPE.CALL_STARTED);
          const callStartedEvent = events.find(re => re.eventType === Constants.VOICE_EVENT_TYPE.CALL_STARTED);
          if (!callStartedEvent || callStartedEvent.dataType !== 'CallResult') {
            this.log(LOG_LEVEL.Warning, fname, 'Could not find a CALL_STARTED event for outbound call!', events);
          } else {
            this.log(LOG_LEVEL.Debug, fname, `Splicing call started event at index: ${callStartedEventIdx}`, { callStartedEvent, events });
            events.splice(callStartedEventIdx, 1);
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const partyIdx = this.getPartyIndex((callStartedEvent?.data as CallResult).call.contact.phoneNumber);
          if (partyIdx < 0) {
            this.log(LOG_LEVEL.Error, fname, `Could not find active party for outbound call command: ${p.additionalDetails.phoneNumber}`);
          }

          this.activeCalls[partyIdx].callType = Constants.CALL_TYPE.OUTBOUND;

          events = events.filter(ev => {
            if (p.pending && ev.dataType === 'CallResult' && promise.pending) {

              if (callStartedEvent && callStartedEvent.dataType === 'CallResult') {
                this.log(LOG_LEVEL.Information, fname, `Resolving ${promise.kind}`, callStartedEvent.data);
                p.resolve?.(callStartedEvent.data);
                setTimeout(() => {
                  this.publishEventToScv(ev, cId);
                }, 0);
              } else {
                this.log(LOG_LEVEL.Information, fname, `Resolving ${promise.kind}`, ev);
                p.resolve?.(ev.data);
              }
              return false;
            }
            return true;
          });

          if (p.pending && callStartedEvent && callStartedEvent.dataType === 'CallResult') {
            if (partyIdx < 0) {
              this.log(LOG_LEVEL.Trace, fname, `Active party cannot be found, cannot force call to be outbound (party=${p.additionalDetails.phoneNumber})`);
            } else {
              this.log(LOG_LEVEL.Information, fname, `Resolving ${promise.kind}`, callStartedEvent);
              p.resolve?.(callStartedEvent.data);
            }
          }
        }
          break;
        case 'BlindTransfer': {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const p = promise as BlindTransferDeferredPromise;
          this.log(LOG_LEVEL.Trace, fname, `Attempting to resolve a blind transfer. ${this.activeCalls.length} activeCalls.`, { activeCalls: this.activeCalls, copy: this.activeCallsCopy });
          if (
            interaction.state === INTERACTION_STATES.Disconnected &&
            (
              this.activeCalls.length === 0 ||
              (
                this.activeCalls.length >= 1 &&
                this.activeParties.some(ap => ap.interactionId === interaction.interactionId)
              )
            )
          ) {
            this.log(LOG_LEVEL.Critical, fname, 'Attempting to resolve a blind transfer.', this.activeCalls);
            let shouldRemoveHolds = false;
            events = events.filter(ev => {
              try {
                if (p.pending && ev.dataType === 'HangupResult' && ev.data.calls[0].callId === p.additionalDetails.call.callId) {
                  const saturatedCall: PhoneCall = saturatePhoneCall(ev.data.calls[0]).saturated;
                  const saturatedResult: ParticipantResult = saturateParticipantResult(new ParticipantResult({
                    callId: saturatedCall.callId,
                    callInfo: saturatedCall.callInfo,
                    initialCallHasEnded: true,
                    phoneNumber: saturatedCall.contact.phoneNumber
                  })).saturated;

                  this.log(LOG_LEVEL.Information, fname, `Resolving ${promise.kind}`, saturatedResult);
                  p.resolve?.(saturatedResult);
                  shouldRemoveHolds = true;
                  return false;
                }
                return true;
              } catch (error) {
                return false;
              }
            });

            // Remove all hold toggle events as well, these may come in when a blind transfer is done,
            // and will confuse Salesforce
            if (shouldRemoveHolds) {
              events = events.filter(ev => ev.dataType !== 'HoldToggleResult');
            }
            this.log(LOG_LEVEL.Critical, fname, `Events after filtering = ${events.length}`, events);
          }
        }
          break;
        }
      }

      this.log(LOG_LEVEL.Loop, fname, `${events.length} events to handle after checking.`, events);
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);
      throw err;
    } finally {
      this.log(LOG_LEVEL.Debug, fname, 'END <<<');
      return events;
    }
  }

  /**
   * Removes ended calls from state before updating Salesforce with the latest events.
   * @param cId
   * @returns list of events to process after other events.
   */
  removeEndedCalls(cId: CID, interaction: IInteraction, events: ScvVoiceEventPayload[]): void {
    const fname = `${this.cname}removeEndedCalls (${cId}): `;

    try {
      const activeCallsAtStart = this.activeCalls.length;
      this.log(LOG_LEVEL.Loop, fname, 'START >>>', { activeCalls: this.activeCalls, activeParties: this.activeParties });
      let hasRemovedCalls = false;
      let removedCall: AmcCall | undefined; // used for sending the end time to DEG
      let removedParty: ActiveParty | undefined;

      let removedPartyIdx = this.activeParties.findIndex(ap => ap.removalDetails.isRemoved);

      while (removedPartyIdx >= 0) {
        const preserveOtherCalls = this.activeParties[removedPartyIdx].removalDetails.preserveOtherCalls;
        this.log(LOG_LEVEL.Loop, fname, `Removing party at index ${removedPartyIdx} and ${preserveOtherCalls ? '' : 'not '} preserving other calls`);
        if (!preserveOtherCalls) {
          removedParty = this.activeParties.splice(removedPartyIdx, 1)[0];
          removedCall = this.activeCalls.splice(removedPartyIdx, 1)[0];
          hasRemovedCalls = true;
        } else {
          removedParty = this.activeParties.splice(removedPartyIdx, 1)[0];

          while (removedPartyIdx < this.activeCalls.length - 1) {
            const callId = this.activeCalls[removedPartyIdx].callId;
            const contactType = this.activeCalls[removedPartyIdx].contact.type;
            this.activeCalls[removedPartyIdx] = saturateAmcCall(this.activeCalls[removedPartyIdx + 1]).saturated;
            this.activeCalls[removedPartyIdx].callId = callId;
            this.activeCalls[removedPartyIdx].contact.type = contactType;

            const updatedCall = this.activeCalls[removedPartyIdx];

            if (!updatedCall.phoneNumber.toLowerCase().includes(updatedCall.contact.name)) {
              updatedCall.phoneNumber = updatedCall.contact.name;
            }

            removedPartyIdx++;
          }

          removedCall = this.activeCalls.pop(); // ! participant to drop
          hasRemovedCalls = true;
          if (this.activeCalls.length) {
            this.activeCalls[0].callAttributes.participantType = Constants.PARTICIPANT_TYPE.INITIAL_CALLER;
          }
        }
        removedPartyIdx = this.activeParties.findIndex(ap => ap.removalDetails.isRemoved);
      }

      if (hasRemovedCalls) {
        this.log(LOG_LEVEL.Information, fname, `${this.activeCalls.length} calls remaining after removal. Publishing event to update the state of the remaining calls`);

        if (this.activeParties.length > 0) {
          this.activeParties[0].participantType = Constants.PARTICIPANT_TYPE.INITIAL_CALLER;
        }

        for (const call of this.activeCalls) {
          call.state = Constants.CALL_STATE.CONNECTED;

          const partyIndex = this.getPartyIndex(call.contact.phoneNumber);
          if (partyIndex === 0) {
            call.callAttributes.participantType = Constants.PARTICIPANT_TYPE.INITIAL_CALLER;
            const callConnectedEvent: ScvVoiceEventPayload = {
              eventType: Constants.VOICE_EVENT_TYPE.CALL_CONNECTED,
              dataType: 'CallResult',
              data: new CallResult({
                call
              })
            };
            this.pushEvent(cId, events, deepCopy(callConnectedEvent));
          } else if (partyIndex > 0) {
            call.callAttributes.participantType = Constants.PARTICIPANT_TYPE.THIRD_PARTY;
            const participantConnectedEvent: ScvVoiceEventPayload = {
              eventType: Constants.VOICE_EVENT_TYPE.PARTICIPANT_CONNECTED,
              dataType: 'ParticipantResult',
              data: new ParticipantResult({
                phoneNumber: call.phoneNumber,
                initialCallHasEnded: false,
                callInfo: call.callInfo,
                callId: call.callId
              })
            };
            this.pushEvent(cId, events, deepCopy(participantConnectedEvent));
          }
        }

        if (this.activeCalls.length > 0) {
          // ! publish a hold toggle result if there are still activeCalls after dropping everything
          const holdToggleEvent: ScvVoiceEventPayload = {
            eventType: Constants.VOICE_EVENT_TYPE.HOLD_TOGGLE,
            dataType: 'HoldToggleResult',
            data: new HoldToggleResult({
              isCustomerOnHold: this.activeCalls[0].callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER ? this.activeCalls[0].callAttributes.isOnHold : false,
              isThirdPartyOnHold: this.activeCalls.some(ac => ac.callAttributes.participantType !== Constants.PARTICIPANT_TYPE.INITIAL_CALLER && ac.callAttributes.isOnHold),
              // calls: [call]
            })
          };
          this.pushEvent(cId, events, deepCopy(holdToggleEvent));
        }
      }

      if (
        interaction.state === INTERACTION_STATES.Disconnected &&
          removedCall !== undefined &&
          this.activeCalls.length === 0 &&
          this.activeParties.length === 0 &&
          this.scs.VoiceCallRecordCreation !== VoiceCallRecordCreationEnum.ClientSideCallCreation
      ) {
        let callEndType = 'CALL END';
        if (activeCallsAtStart > 1) {
          callEndType = 'WARM TRANSFER';
        } else if (this.promisesToResolve.find(p => p.kind === 'BlindTransfer')) {
          callEndType = 'BLIND TRANSFER';
        }

        interaction.details?.setField('CallEndType', 'CallEndType', 'CallEndType', callEndType);
        const attributes = this.communicationService.processVoiceCallRecordToCADMapping(interaction);

        this.log(LOG_LEVEL.Trace, fname, `Interaction is disconnected, removing call with vendorCallKey ${removedCall.vendorCallKey ?? this.getVendorCallKeyFromInteraction(interaction)}`, attributes);
        this.communicationService.updateCall(
          cId,
          interaction.interactionId,
          removedCall.vendorCallKey ?? this.getVendorCallKeyFromInteraction(interaction),
          removedCall.startTime,
          new Date(),
          attributes
        );
      }

    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', error);
    } finally {
      this.log(LOG_LEVEL.Loop, fname, 'END <<<', { activeCalls: this.activeCalls, activeParties: this.activeParties });
    }
  }

  createNewParties(cId: CID, extractedParties: string[], interaction: Ensure<IInteraction, 'interactionId' | 'scenarioId' | 'state'>): ActiveParty[] {
    const fname = this.cname + `createNewParties (${cId})`;

    const unknowns = ['UNKNOWN', 'EXTERNAL UNKNOWN'];

    try {
      this.log(LOG_LEVEL.Trace, fname, 'START >>>');

      const newParties: ActiveParty[] = [];

      let newPartyStrings = extractedParties.filter(ep => this.getPartyIndex(ep) < 0);

      if (this.activeParties.some(ap => unknowns.includes(ap.phoneNumber.toUpperCase())) && extractedParties.some(ep => this.getPartyIndex(ep) >= 0)) {
        for (const newParty of newPartyStrings) {
          const unknownIndex = this.activeParties.findIndex(ap => unknowns.includes(ap.phoneNumber.toUpperCase()));

          if (unknownIndex >= 0 && !unknowns.includes(newParty.toUpperCase())) {
            this.updateContact(cId, unknownIndex, newParty, interaction.interactionId, interaction.scenarioId);
          }
        }

        newPartyStrings = extractedParties.filter(ep => this.getPartyIndex(ep) < 0);
      }

      const alreadyAddedInitialCaller = this.activeParties.length > 0;
      for (const party of newPartyStrings) {
        const newParty: ActiveParty = {
          partyId: party,
          phoneNumber: party,
          name: party,
          interactionId: interaction.interactionId,
          scenarioId: interaction.scenarioId,
          participantType: alreadyAddedInitialCaller ? Constants.PARTICIPANT_TYPE.THIRD_PARTY : Constants.PARTICIPANT_TYPE.INITIAL_CALLER,
          removalDetails: {
            isRemoved: false,
            preserveOtherCalls: true
          },
          degCallAttributes: this.communicationService.processVoiceCallRecordToCADMapping(interaction)
        };

        newParties.push(newParty);
      }

      if (newParties.length > 0) {
        this.log(LOG_LEVEL.Information, fname, `Created ${newParties.length} new parties`, newParties);
      }

      return newParties;
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);

      throw err;
    } finally {
      this.log(LOG_LEVEL.Trace, fname, 'END <<<');
    }
  }

  private async publishEventToScv(event: ScvVoiceEventPayload, cId?: CID, timeout?: number): Promise<void> {
    const fname = this.cname + `publishEventToScv${cId ? ` (${cId}): ` : ''}`;

    try {
      this.log(LOG_LEVEL.Trace, fname, 'START >>>');

      const eventCopy = deepCopy(event); // make a deep copy of all events incase that references are changed later
      this.log(LOG_LEVEL.Information, fname, `Publishing event: ${eventCopy.eventType}`, eventCopy);

      if (timeout) {
        // ! If for some reason we need to delay the event, supply a timeout to this function
        setTimeout(() => {
          this.bridgeEventsService?.sendEvent(AMC_EVENT_NAMES.PUBLISH_EVENT, eventCopy);
        }, timeout);
      } else {
        this.bridgeEventsService?.sendEvent(AMC_EVENT_NAMES.PUBLISH_EVENT, eventCopy);
      }
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);

      throw err;
    } finally {
      this.log(LOG_LEVEL.Trace, fname, 'END <<<');
    }
  }

  private pushEvent(cId: CID, eventList: ScvVoiceEventPayload[], event: ScvVoiceEventPayload) {
    const fname = this.cname + `pushEvent(${cId})`;
    try {
      eventList.push(event);
      this.log(LOG_LEVEL.Trace, fname, `${eventList.length} in list after pushing ${event.eventType}.`, { event, eventList });
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to push event to list.', error);
    }
  }

  /*
   * Functions to handle interactions from DaVinci
   */
  protected async onInteraction(interaction: IInteraction): Promise<SearchRecords> {
    const cId = generateCID();
    const fname = this.cname + `onInteraction(${cId}):`;

    try {

      if (interaction.transcripts) {
        this.log(LOG_LEVEL.Debug, fname, 'Interaction has transcript.');
        this.communicationService.sendTranscription(cId, interaction, this.getVendorCallKeyFromInteraction(interaction));
      } else {

        this.formatAniDnis(interaction);

        if (
          interaction.state !== INTERACTION_STATES.Disconnected &&
          this.pendingMergePromise !== undefined &&
          this.pendingMergePromise.additionalDetails.scenarioId === interaction.scenarioId
        ) {
          this.pendingMergePromise.resolve!(true);
          this.log(LOG_LEVEL.Debug, fname, `Pending merge scenarioId ${interaction.scenarioId} has been cleared.`);
        }

        const interactionToSend: IEventQueueInteraction = {
          type: EventQueueType.INTERACTION,
          interaction,
          processId: cId,
          timestamp: Date.now()
        };

        if (!this.prevActiveCleared && this.prevActiveCalls.length > 0 && this.prevActiveParties.length > 0) {
          this.log(LOG_LEVEL.Debug, fname, `Previous Call State Found and within timeout, storing interaction to list and waiting until timeout to determine how to restore the state. InteractionId: ${interaction.interactionId}, ScenarioId: ${interaction.scenarioId}, State: ${interaction.state}`, interaction);

          this.eventQueue.push(interactionToSend);

          if (!this.uniqueInteractionIds.find(i => i === interaction.interactionId)) {
            this.uniqueInteractionIds.push(interaction.interactionId);
          }

          const expectedUniqueIds = new Set(this.prevActiveParties.map(p => p.interactionId)).size;
          if (this.uniqueInteractionIds.length >= expectedUniqueIds) {
            this.log(LOG_LEVEL.Debug, fname, `All expected interactions received (${this.uniqueInteractionIds.length}/${expectedUniqueIds}). Skipping remaining timeout and reconciling immediately.`);
            clearTimeout(this.localStorageCheckTimeoutId);
            this.compareInteractionsToPrevState();
          }
        } else if (this.eventQueue.length > 0) {
          this.log(LOG_LEVEL.Debug, fname, `${this.eventQueue.length} events already in queue, appending to the end. InteractionId: ${interaction.interactionId}, ScenarioId: ${interaction.scenarioId}, State: ${interaction.state}`, interaction);

          this.eventQueue.push(interactionToSend);

        } else if (this.eventQueue.length === 0 ) {
          this.log(LOG_LEVEL.Debug, fname, `Queue is empty, pushing interaction onto queue and starting processing. InteractionId: ${interaction.interactionId}, ScenarioId: ${interaction.scenarioId}, State: ${interaction.state}`, interaction);

          this.eventQueue.push(interactionToSend);

          this.processEventsQueue();
        } else {
          this.log(LOG_LEVEL.Warning, fname, 'Something went wrong.');
        }

      }
      return new SearchRecords();
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', error);
      throw error;
    }
  }

  async processInteraction(cId: CID, interaction: IInteraction): Promise<boolean> {
    const fname = this.cname + `processInteraction(${cId}):`;
    this.pendingMergePromise = undefined;
    let removeInteraction = true;

    try {
      if (!this.interactionIsValid(interaction)) {
        throw new Error('Interaction is not valid');
      }

      const interactionId = interaction.interactionId;
      const scenarioId = interaction.scenarioId;

      this.log(LOG_LEVEL.Debug, fname, `START [${INTERACTION_STATES[interaction.state]}]>>>`, interaction);
      this.log(LOG_LEVEL.Critical, fname, `Processing Interaction\nInteractionId: ${interactionId}\nScenarioId: ${scenarioId}\nState: ${interaction.state}\nPresence: ${this.currentPresence}\nReason: ${this.currentReason ?? ''}`, { interaction, calls: this.activeCalls, parties: this.activeParties });

      // ! 1) Pause and wait on disconnect to catch a merge or warm transfer
      let pendingMerge = false;
      if (interaction.state === INTERACTION_STATES.Disconnected && !this.promisesToResolve.some(pr => pr.kind === 'Hangup')) {
        this.log(LOG_LEVEL.Debug, fname, `Waiting for next interaction event for scenarioId ${scenarioId} to determine how to handle the disconnect`, interaction);

        this.pendingMergePromise = new PendingMergeDeferredPromise({
          interactionId: interaction.interactionId,
          scenarioId: interaction.scenarioId
        });

        setTimeout(() => {
          if (this.pendingMergePromise !== undefined) {
            this.log(LOG_LEVEL.Debug, fname, 'Pending Merge Time Out over');
            this.pendingMergePromise?.resolve!(false);
          }
        }, this.scs.promiseTimeouts['PendingMerge']);

        pendingMerge = await this.pendingMergePromise.promise;

        this.pendingMergePromise = undefined;

        if (pendingMerge) {
          this.log(LOG_LEVEL.Critical, fname, 'Pending connected interaction detected. Requeuing disconnected interaction to be processed later.', interaction);

          const idx = this.eventQueue.findIndex(nextI =>
            nextI.type === EventQueueType.INTERACTION &&
            nextI.interaction.scenarioId === interaction.scenarioId &&
            nextI.interaction.state !== INTERACTION_STATES.Disconnected
          );
          if (idx === -1) {
            this.log(LOG_LEVEL.Error, fname, 'Index for the next interaction cannot be found despite the pendingMerPromise being resolved. How is this possible?', this.eventQueue);
          }

          const nextEventQueueItem = this.eventQueue.splice(idx, 1)[0];
          const nextInteraction: IInteraction = (nextEventQueueItem as IEventQueueInteraction).interaction;

          if (interaction.interactionId === nextInteraction.interactionId && interaction.scenarioId === nextInteraction.scenarioId) {
            // Push the disconnected interaction to the front of the queue
            this.log(LOG_LEVEL.Critical, fname, 'Same interactionId and scenarioId. Processing the next interaction and requeing the disconnected interaction', { nextInteraction, interaction });
            await this.processInteraction(nextEventQueueItem.processId, nextInteraction);
            removeInteraction = false;
            return removeInteraction;
          } else {
            this.log(LOG_LEVEL.Critical, fname, 'Processing next connected interaction before this disconnected interaction.', { nextInteraction, interaction });
            await this.processInteraction(nextEventQueueItem.processId, nextInteraction);
          }
        } else {
          this.log(LOG_LEVEL.Trace, fname, `No pending connected interaction found for interactionId: ${interaction.interactionId} and scenarioId: ${interaction.scenarioId}`);
        }
      }

      // ! 1.1) If the interaction is disconnected and no active parties have this interactionId, skip processing
      if (interaction.state === INTERACTION_STATES.Disconnected && !this.activeParties.some(ap => ap.interactionId === interaction.interactionId)) {
        this.log(LOG_LEVEL.Critical, fname, `No active parties found for disconnected interactionId: ${interaction.interactionId}. Skipping processing.`, { interaction, activeParties: this.activeParties });
        return removeInteraction;
      }

      // ! 2) Get the parties that are directly tied to this singular interaction
      /** The list of partyIds on the current interaction. */
      const extractedPartyIds = this.extractPartyIds(interaction);
      this.log(LOG_LEVEL.Debug, fname, `Extracted party IDs for interaction ${interactionId}: ${extractedPartyIds.join(', ')}`, interaction);

      // Any active parties that match the scenarioId and interactionId that aren't
      // in the extracted parties list have either been removed, or the call may have
      // been updated by receiving a warm transfer.
      /** The parties in activeParties that are associated to the current interaction, but no longer present on it. */
      const filteredParties = this.activeParties.filter(ap =>
        ap.interactionId === interaction.interactionId &&
        ap.scenarioId === interaction.scenarioId &&
        !extractedPartyIds.some(ep =>
          ep === ap.phoneNumber ||
          ep === ap.name ||
          ep === ap.partyId
        )
      );

      // ! 2.1) Check to see if the remaining call needs to
      // If the number of active calls we have is 1, and the number of parties filtered above is 1,
      // then we know that a warm transfer has just been received. Just update the party with the
      // new details and continue
      if (extractedPartyIds.length === 1 && filteredParties.length === 1 && this.activeCalls.length === 1) {
        this.updateContact(cId, 0, extractedPartyIds[0], interaction.interactionId, interaction.scenarioId);

      } else if (extractedPartyIds.length > 0 && filteredParties.length > 0 && this.activeCalls.length > 1) {
        // If there are at least 2 parties and all parties have unique interaction Ids, then we know we need to
        // check if the incoming interaction matches an existing party. If there is a matching party we can assume
        // that it is just the number changing and update it accordingly.

        // get the existing parties that are present in this interaction
        /** The parties in activeParties that are associated to the current interaction. */
        const existingParties = this.activeParties.filter(ap =>
          ap.interactionId === interaction.interactionId &&
          ap.scenarioId === interaction.scenarioId &&
          extractedPartyIds.some(ep =>
            ep === ap.phoneNumber ||
            ep === ap.name ||
            ep === ap.partyId
          )
        );

        this.log(LOG_LEVEL.Trace, fname, `${existingParties.length} Active Parties for interactionId: ${interaction.interactionId}`, existingParties);

        // TODO: For this issue we do not detect that the customer dropped while the conference was held. The reason this only happens on a held conference and not a connected one needs to be identified.
        if (existingParties.length === extractedPartyIds.length) {
          // ! We miss the held customer dropping a conference because the above if resolves to true.
          this.log(LOG_LEVEL.Trace, fname, 'All parties are accounted for, no changes needed.');
        } else if (extractedPartyIds.length > existingParties.length) {

          this.log(LOG_LEVEL.Trace, fname, `${extractedPartyIds.length} parties on the interaction and ${existingParties.length} parties accounted for in the current list of active parties.`);

          for (let i = 0; i < extractedPartyIds.length; i++) {

            const currentPartyExists = this.activeParties.some(ep =>
              ep.interactionId === interaction.interactionId &&
              ep.scenarioId === interaction.scenarioId && (
                ep.phoneNumber === extractedPartyIds[i] ||
                ep.name === extractedPartyIds[i] ||
                ep.partyId === extractedPartyIds[i]
              )
            );

            if (!currentPartyExists) {
              this.log(LOG_LEVEL.Loop, fname, `Party does not exists for ${extractedPartyIds[i]}`);
              if (filteredParties.length > 0) {
                this.log(LOG_LEVEL.Loop, fname, `${filteredParties[0].phoneNumber} is not on the current interaction.`);
                const filteredIdx = this.getPartyIndex(filteredParties[0].phoneNumber);
                if (filteredIdx) {
                  this.log(LOG_LEVEL.Trace, fname, `Updating party ${filteredParties[0].phoneNumber} with index ${filteredIdx} to be party ${extractedPartyIds[i]}`);
                  this.updateContact(cId, filteredIdx, extractedPartyIds[i], interaction.interactionId, interaction.scenarioId);
                  filteredParties.shift();
                }
              }
            } else {
              this.log(LOG_LEVEL.Loop, fname, `Party exists for ${extractedPartyIds[i]}`);
            }
          }
        }
      }

      // ! 3) create parties if there isn't an existing one the corresponds to a party from this interaction
      let newParties = this.createNewParties(cId, extractedPartyIds, interaction);

      if (
        newParties.length > 0 &&
        this.promisesToResolve.find(p => p.kind === 'BlindTransfer' && p.pending === true) &&
        interaction.state !== INTERACTION_STATES.Disconnected
      ) {
        this.log(LOG_LEVEL.Debug, fname, 'New parties are trying to be added while there is a pending blind transfer. Ignoring them.', newParties);
        newParties = [];
        this.ignoredInteractionId = interaction.interactionId;
      } else if (this.ignoredInteractionId === interaction.interactionId) {
        this.log(LOG_LEVEL.Debug, fname, 'Continuing to ignore this interactionId', newParties);
        newParties = [];
      } else {
        this.log(LOG_LEVEL.Trace, fname, 'Not ignoring');
      }

      this.activeParties.push(...newParties);

      if (this.activeParties.length >= 3) {
        this.log(LOG_LEVEL.Warning, fname, `${this.activeParties.length} parties. This total should normally never exceed 2.`);
      }

      // ! 3.1) Updating Extracted parties from the interaction with the interactionId and scenarioId
      extractedPartyIds.forEach(ep => {
        const partyIdx = this.getPartyIndex(ep);
        if (partyIdx >= 0) {
          this.activeParties[partyIdx].interactionId = interactionId;
          this.activeParties[partyIdx].scenarioId = scenarioId;
          if (partyIdx === 0) {
            this.activeParties[partyIdx].participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER;
          } else {
            this.activeParties[partyIdx].participantType === Constants.PARTICIPANT_TYPE.THIRD_PARTY;
          }
        }
      });

      this.log(LOG_LEVEL.Debug, fname, `${this.activeParties.length} active parties after update.`, this.activeParties);

      const newPhoneCalls: AmcCall[] = await this.createNewPhoneCalls(cId, interaction, newParties);
      this.activeCalls.push(...newPhoneCalls);

      if (this.activeCalls.length >= 3) {
        this.log(LOG_LEVEL.Warning, fname, `${this.activeCalls.length} calls. This total should normally never exceed 2.`);
      }

      // ! 4) Update the parties on the corresponding activeCall
      // ToDo: Determine if this is needed anymore
      for (const party of extractedPartyIds) {
        const partyIdx = this.getPartyIndex(party);
        if (partyIdx < 0) {
          throw new Error('Could not find active party when updating DaVinci party indices');
        }
        if (partyIdx >= this.activeCalls.length) {
          throw new Error('Could not find active call when updating DaVinci party indices');
        }

        if (interaction.multiPartyState && interaction.details?.fields['conferenceParties'].Value) {
          const partiesObj = JSON.parse(interaction.details.fields['conferenceParties'].Value);

          this.activeCalls[partyIdx].parties =
            Object.keys(partiesObj).map(key => parseInt(key)).filter(key => !isNaN(key)).sort((a, b) => a - b).map(key => String(partiesObj[key]));
        } else {
          this.activeCalls[partyIdx].parties = extractedPartyIds;
        }
      }

      // ! 5) Update call controls and sort the Parties and Calls
      this.updateCallControlsBasedOnState(interaction, cId);
      this.sortPartiesAndCalls(interaction, cId);

      // ! 6) Determine what events to raise to Salesforce
      let events = this.determineEventsAndUpdateCalls(interaction, cId);

      // ! 7) Remove Ended Calls
      // ToDo: seperate the logic for removing ended calls from the logic for removing ended parties
      this.removeEndedCalls(cId, interaction, events);
      this.log(LOG_LEVEL.Trace, fname, `${events.length} events to be processed.`, { events, interaction, calls: this.activeCalls, parties: this.activeParties });

      this.log(LOG_LEVEL.Debug, fname, `${this.activeCalls.length} active calls after update.`, this.activeCalls);

      // ! 8) Resolve solicited events
      if (this.promisesToResolve.some(p => p.pending)) {
        this.log(LOG_LEVEL.Trace, fname, `Resolving solicited [${INTERACTION_STATES[interaction.state]}]`);
        this.log(LOG_LEVEL.Trace, fname, `${events.length} remaining events before resolving solicited commands.`, events);
        events = this.resolveSolicitedCommands(cId, interaction, events);
        this.log(LOG_LEVEL.Trace, fname, `${events.length} remaining events after resolving solicited commands.`, events);
      } else {
        this.log(LOG_LEVEL.Trace, fname, `No solicited events to resolve for interactionId: ${interactionId}, scenarioId: ${scenarioId}, state: ${interaction.state}`);
      }

      // ! 9) Raise any remaining unsolicited events
      if (events.length > 0) {
        this.log(LOG_LEVEL.Trace, fname, `Raising ${events.length} unsolicited events for interactionId ${interactionId}, scenarioId ${scenarioId}, state ${interaction.state}`, events);
        for (const event of events) {
          this.log(LOG_LEVEL.Warning, fname, `Raising ${event.eventType} [${INTERACTION_STATES[interaction.state]}]`, event);
          this.publishEventToScv(event, cId);
        }
      } else {
        this.log(LOG_LEVEL.Trace, fname, `No unsolicited events to raise for interactionId: ${interactionId}, scenarioId: ${scenarioId}, state: ${interaction.state}`);
      }

      // ! 10) Store a copy of the state to be used for getActiveCalls
      this.activeCallsCopy = deepCopy(this.activeCalls); // Deep copy for getActiveCalls to reference asynchonously incase the state is being updated by a different interaction.
      this.storeCallsAndPartiesToLocalStorage(cId);
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);
    } finally {
      this.log(LOG_LEVEL.Debug, fname, `Finished processing Interaction\nInteractionId: ${interaction.interactionId}\nScenarioId: ${interaction.scenarioId}\nState: ${interaction.state}\nPresence: ${this.currentPresence}\nReason: ${this.currentReason ?? ''}`, { interaction, calls: this.activeCalls, parties: this.activeParties });
    }
    return removeInteraction;
  }

  storeCallsAndPartiesToLocalStorage(cId?: CID): void {
    const fname = this.cname + `storeCallsAndPartiesToLocalStorage${cId ? ` (${cId})` : ''}`;

    try {
      this.log(LOG_LEVEL.Trace, fname, `Storing ${this.activeCalls.length} active calls and ${this.activeParties.length} active parties to local storage`, {activeCalls: this.activeCalls, activeParties: this.activeParties});

      this.storageService.updateActiveCalls(this.activeCalls);
      this.storageService.updateActiveParties(this.activeParties);
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);

      throw err;
    } finally {
      this.log(LOG_LEVEL.Debug, fname, 'END <<<');
    }
  }

  loadCallsAndPartiesFromLocalStorage(cId?: CID): void {
    const fname = this.cname + `loadCallsAndPartiesFromLocalStorage${cId ? ` (${cId})` : ''}`;

    try {
      this.log(LOG_LEVEL.Debug, fname, 'START >>>');

      this.prevActiveCalls = this.storageService.getActiveCalls();
      this.prevActiveParties = this.storageService.getActiveParties();

      this.log(LOG_LEVEL.Debug, fname, `Loaded ${this.prevActiveCalls.length} previous active calls and ${this.prevActiveParties.length} previous active parties`, {prevActiveCalls: this.prevActiveCalls, prevActiveParties: this.prevActiveParties});

      if (this.prevActiveCalls.length > 0 && this.prevActiveParties.length > 0) {
        this.localStorageCheckTimeoutId = setTimeout(this.compareInteractionsToPrevState.bind(this), this.scs.localStorageCheckTimeout);
      } else {
        this.log(LOG_LEVEL.Debug, fname, 'No previous calls or parties in local storage, skipping timeout.');
        this.prevActiveCleared = true;
      }

    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);
    }
  }

  compareInteractionsToPrevState(): void {
    const fname = this.cname + 'compareInteractionsToPrevState';

    try {
      if (this.prevActiveCleared) {
        this.log(LOG_LEVEL.Debug, fname, 'Previous state already cleared, skipping.');
        return;
      }

      this.log(LOG_LEVEL.Debug, fname, `Comparing incoming interactions to previous state. Previous calls: ${this.prevActiveCalls.length}, Previous parties: ${this.prevActiveParties.length}`, {prevActiveCalls: this.prevActiveCalls, prevActiveParties: this.prevActiveParties, uniqueInteractionIds: this.uniqueInteractionIds, eventQueue: this.eventQueue});
      if (this.uniqueInteractionIds.length > 0) {
        // ! There are interactions in the queue, determine what needs to be restored

        if (this.uniqueInteractionIds.length < this.prevActiveCalls.length) {
          this.dropPrevPartyCall();
        } else if (this.uniqueInteractionIds.length > this.prevActiveCalls.length) {
          this.log(LOG_LEVEL.Trace, fname, 'More Interactions than previous calls.');
          // ! Nothing extra needs to be done in this scenario, the extra calls and parties should be added by onInteraction
        } else {
          this.log(LOG_LEVEL.Trace, fname, 'Same number of interactionIds as previous calls.');
        }

        if (this.prevActiveCalls.length > 0 && this.prevActiveParties.length > 0) {
          this.restorePartiesCalls();
        } else {
          this.log(LOG_LEVEL.Trace, fname, 'No previous calls or parties to restore.');
        }

      } else {
        // ! No interactions in the queue, clean up the previous state
        this.dropAllPrevPartiesCalls();
      }
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);
    } finally {
      this.removePrevState();
      this.processEventsQueue();
    }
  }

  async processEventsQueue(): Promise<void> {
    const fname = this.cname + 'processEventsQueue';
    try {
      this.log(LOG_LEVEL.Critical, fname, `Processing queue of events. ${this.eventQueue.length} events in the queue`, this.eventQueue);

      while (this.eventQueue.length > 0) {
        const event = this.eventQueue[0];
        this.log(LOG_LEVEL.Information, fname, `processing event of type ${event.type}`, event);

        if (event.type === EventQueueType.INTERACTION) {
          const interaction = event.interaction;
          if (interaction) {
            this.log(LOG_LEVEL.Debug, fname, `calling processInteraction for interactionId: ${interaction.interactionId}, scenarioId: ${interaction.scenarioId}, state: ${interaction?.state}`, interaction);
            const shouldRemove = await this.processInteraction(event.processId, interaction);
            if (shouldRemove) {
              this.log(LOG_LEVEL.Loop, fname, `Removing interactionId: ${interaction.interactionId} from the queue.`);
              this.eventQueue.shift();
            } else {
              this.log(LOG_LEVEL.Loop, fname, `Not removing interactionId: ${interaction.interactionId} from the queue.`);
            }
          }

        } else if (event.type === EventQueueType.PRESENCE) {
          const presenceEvent = event;
          this.log(LOG_LEVEL.Debug, fname, `Processing presence event: presence=${presenceEvent.presence}, reason=${presenceEvent.reason}`);

          await this.processPresenceFromFramework(event.processId, event.presence, event?.reason, event?.initiatingApp);

          this.eventQueue.shift();
          this.log(LOG_LEVEL.Loop, fname, 'Presence event published and removed from queue.');
        }

        this.log(LOG_LEVEL.Debug, fname, `Processed event. ${this.eventQueue.length} events left in the queue`, this.eventQueue);
        this.log(LOG_LEVEL.Trace, fname, `Active Calls: ${this.activeCalls.length}, Active Parties: ${this.activeParties.length}`, {activeCalls: this.activeCalls, activeParties: this.activeParties});
      }
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);
    }
  }

  dropPrevPartyCall(): void {
    const fname = this.cname + 'dropPrevPartyCall';

    try {
      this.log(LOG_LEVEL.Warning, fname, 'Fewer Interactions than previous calls. Identifying the party / call to drop.');
      // Find the first party that is not in the interaction queue

      const partyIndexToDrop = this.prevActiveParties.findIndex(prevParty => !this.uniqueInteractionIds.includes(prevParty.interactionId));
      if (partyIndexToDrop === -1) {
        throw new Error('Could not find party to drop');
      }

      this.dropPrevPartyCallByIndex(partyIndexToDrop);

    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);
    }
  }

  dropAllPrevPartiesCalls(): void {
    const fname = this.cname + 'dropAllPartiesCalls';

    try {
      const prevActivePartiesLength = this.prevActiveParties.length;
      if (prevActivePartiesLength > 0) {
        this.log(LOG_LEVEL.Debug, fname, `Dropping ${prevActivePartiesLength} parties and calls`);
        for (let i = prevActivePartiesLength; i >= 0; i--) {
          // ! Drop parties in reverse order
          this.log(LOG_LEVEL.Debug, fname, `Dropping party and call at index ${i}`);
          this.dropPrevPartyCallByIndex(i);
        }
      }
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);

    }
  }

  dropPrevPartyCallByIndex(partyIndexToDrop: number) {
    const fname = this.cname + 'dropPrevPartyCallByIndex';

    try {
      this.log(LOG_LEVEL.Debug, fname, `Dropping party and call at index ${partyIndexToDrop}`);

      const partyToDrop = this.prevActiveParties[partyIndexToDrop];
      const callToDrop = this.prevActiveCalls[partyIndexToDrop];

      if (partyToDrop && callToDrop) {
        this.log(LOG_LEVEL.Trace, fname, 'Dropping party and call', {partyToDrop, callToDrop});

        this.prevActiveParties.splice(partyIndexToDrop, 1);
        this.prevActiveCalls.splice(partyIndexToDrop, 1);

        if (this.prevActiveCalls.length === 0 && this.scs.VoiceCallRecordCreation !== VoiceCallRecordCreationEnum.ClientSideCallCreation) {
          this.log(LOG_LEVEL.Trace, fname, `Raising UpdateCall to DEG for interactionId: ${partyToDrop.interactionId}, scenarioId: ${partyToDrop.scenarioId}`, partyToDrop);

          let callEndTypeKey = 'CallEndType';
          if (this.scs.VoiceCallRecordToCAD) {
            for (const [key, value] of Object.entries(this.scs.VoiceCallRecordToCAD)) {
              if (value === 'CallEndType') {
                callEndTypeKey = key;
                break;
              }
            }
          }

          let updatedAttributes = partyToDrop.degCallAttributes;
          try {
            const parsed = JSON.parse(partyToDrop.degCallAttributes || '{}');
            parsed[callEndTypeKey] = 'PAGE RELOADED';
            updatedAttributes = JSON.stringify(parsed);
          } catch (parseErr) {
            this.log(LOG_LEVEL.Warning, fname, 'Could not parse degCallAttributes to inject CallEndType', parseErr);
          }

          this.communicationService.updateCall(
            'page load',
            partyToDrop.interactionId,
            callToDrop.vendorCallKey,
            callToDrop.startTime,
            new Date(),
            updatedAttributes
          );
        } else {
          this.log(LOG_LEVEL.Trace, fname, `Not Initial Caller. interactionId: ${partyToDrop.interactionId}, scenarioId: ${partyToDrop.scenarioId}`, partyToDrop);
        }
      }

      this.log(LOG_LEVEL.Trace, fname, `Remaining calls : ${this.prevActiveCalls.length}, Remaining parties: ${this.prevActiveParties.length}`, {prevActiveCalls: this.prevActiveCalls, prevActiveParties: this.prevActiveParties});
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);
    }
  }

  restorePartiesCalls(): void {
    const fname = this.cname + 'restorePartiesCalls';
    try {
      this.log(LOG_LEVEL.Debug, fname, `Restoring ${this.prevActiveCalls.length} parties and calls to active state.`);

      this.activeCalls.push(...this.prevActiveCalls);
      this.activeParties.push(...this.prevActiveParties);

      this.publishActiveCallsToSalesforce();

    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);
    }
  }

  private removePrevState(): void {
    const fname = this.cname + 'removePrevState';

    try {
      this.log(LOG_LEVEL.Debug, fname, 'Clearing prevActiveCalls and prevActiveParties');
      this.prevActiveCalls.length = 0;
      this.prevActiveParties.length = 0;

      // this.eventQueue = []; // ! If this is reused for queuing interactions for onInteraction remove this and pop the interations once they have been processed.
      this.uniqueInteractionIds.length = 0;
      this.localStorageCheckTimeoutId = undefined;

    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);

      throw err;
    } finally {
      this.prevActiveCleared = true;
    }
  }

  private async createNewPhoneCalls(cId: CID, interaction: Readonly<Ensure<IInteraction, 'state' | 'interactionId' | 'scenarioId'>>, parties: Readonly<ActiveParty>[]): Promise<AmcCall[]> {
    const fname = this.cname + 'createNewPhoneCalls';

    try {
      this.log(LOG_LEVEL.Debug, fname, 'START >>> INT = ' + interaction.interactionId + ', party #=' + parties.length);

      const newCalls: AmcCall[] = [];
      for (const party of parties) {
        this.log(LOG_LEVEL.Trace, fname, `Creating new call for party ${party.partyId}`);

        const contact = new Contact({
          id: party.partyId,
          type: Constants.CONTACT_TYPE.PHONENUMBER,
          name: party.partyId,
          phoneNumber: party.phoneNumber,
          prefix: undefined,
          extension: undefined,
          endpointARN: undefined,
          queue: undefined,
          availability: undefined,
          recordId: undefined,
          description: undefined,
          queueWaitTime: undefined
        });

        const callAttributes = new PhoneCallAttributes({
          voiceCallId: undefined, // ! This is the only location where the ID is being set in the latest Salesforce Documentation. We may need to set the vendorCallKey/DEG Call Id here in the future.
          // voiceCallId: interaction.details?.getField('SalesforceCallId').Value ?? undefined,
          participantType: party.participantType,
          dialerType: Constants.DIALER_TYPE.NONE,
          parentId: undefined,
          isOnHold: interaction.state === INTERACTION_STATES.OnHold,
          hasSupervisorBargedIn: false
        });

        const callInfo = this.normalControls(interaction);

        let callType: Constants.CALL_TYPE;

        // ToDo: Update this logic to account for INTERNAL calls
        // This isn't broken at the moment because of the lack of INTERNAL support,
        // but in the future it could cause issues if Salesforce changes how
        // they handle call types.
        if (party.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER) {
          switch (interaction.direction) {
          case INTERACTION_DIRECTION_TYPES.Inbound:
            callType = Constants.CALL_TYPE.INBOUND;
            break;
          case INTERACTION_DIRECTION_TYPES.Outbound:
            callType = Constants.CALL_TYPE.OUTBOUND;
            break;
          case INTERACTION_DIRECTION_TYPES.Internal:
            if (interaction.state === INTERACTION_STATES.Alerting) {
              callType = Constants.CALL_TYPE.INBOUND;
            } else if (interaction.state === INTERACTION_STATES.Initiated) {
              callType = Constants.CALL_TYPE.OUTBOUND;
            } else {
              this.log(LOG_LEVEL.Debug, fname, `Internal call detected that isn't Alerting/Initiated. Reading defaultUnknownDirection ${this.scs.defaultUnkownDirection}`);
              callType = this.scs.defaultUnkownDirection ?? Constants.CALL_TYPE.INBOUND;
            }
          }
        } else {
          // ! This may need to change in the future.
          // For now we will just assume that the add participant is type transfer.
          callType = Constants.CALL_TYPE.TRANSFER;
        }
        this.log(LOG_LEVEL.Trace, fname, `New Call is ${callType}`);

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const conferencePartiesRaw = interaction.details?.fields?.conferenceParties?.Value && JSON.parse(interaction.details?.fields?.conferenceParties.Value);
        const conferenceParties: string[] = [];

        if (conferencePartiesRaw) {
          conferenceParties.push(...(Object.values(conferencePartiesRaw).map(val => String(val))));
        }

        const vendorCallKey: string = this.getVendorCallKeyFromInteraction(interaction);
        let callId: string = vendorCallKey + `_${Number(new Date())}`;
        const existingCallId = this.getExistingCallId(interaction, vendorCallKey); // ! this will effectively be like the old check for if SalesforceCallId had been set on the interaction or not

        let startTime: Date = new Date();

        if (
          !existingCallId &&
          this.activeCalls.length === 0 &&
          this.scs.VoiceCallRecordCreation !== VoiceCallRecordCreationEnum.ClientSideCallCreation
        ) {
          this.log(LOG_LEVEL.Debug, fname, 'No existingCallId or active calls, creating a new call via DEG', { interactionId: interaction.interactionId, scenarioId: interaction.scenarioId, state: interaction.state, direction: interaction.direction, vendorCallKey: vendorCallKey, startTime: startTime });
          callId = await this.communicationService.createCall(cId, interaction, startTime, vendorCallKey, callType === Constants.CALL_TYPE.INBOUND, party.degCallAttributes);

        } else if (existingCallId) {
          // if there is an existingCallId then activeCalls should have at least one call
          this.log(LOG_LEVEL.Trace, fname, `existingCallId found for interactionId: ${interaction.interactionId} and vendorCallKey: ${vendorCallKey}, using existing callId: ${existingCallId}`);
          callId = existingCallId + `_${Number(new Date())}`; // appending timestamp to ensure uniqueness in case of multiple calls in the same scenario
          startTime = this.activeCalls[0].startTime; // child calls should have the same start time as the parent call
        } else {
          this.log(LOG_LEVEL.Trace, fname, `No existingCallId for for interactionId: ${interaction.interactionId} and vendorCallKey: ${vendorCallKey}, using existing callId: ${existingCallId}`);
        }

        const phoneCall = new AmcCall({
          callId: callId,
          callType,
          contact,
          state: interaction.state === INTERACTION_STATES.Alerting || interaction.state === INTERACTION_STATES.Initiated ?
            Constants.CALL_STATE.RINGING : Constants.CALL_STATE.CONNECTED,
          callAttributes,
          phoneNumber: party.phoneNumber,
          callInfo,
          reason: undefined,
          closeCallOnError: true,
          agentStatus: undefined,
          agentARN: undefined,
          tag: this.randomTag(),
          parties: conferenceParties,
          alreadyRaisedToSalesforce: false,
          vendorCallKey,
          startTime: startTime,
        });

        if (this.activeCalls.length > 0) {
          phoneCall.callId += `_${this.activeCalls.length + newCalls.length}`;
        }

        this.log(LOG_LEVEL.Trace, fname, `New call created for ${party.partyId}`, phoneCall);

        newCalls.push(phoneCall);
      }

      if (newCalls.length > 0) {
        this.log(LOG_LEVEL.Critical, fname, `Created ${newCalls.length} new phone calls for interactionId: ${interaction.interactionId}, scenarioId: ${interaction.scenarioId}, state: ${interaction.state}`, newCalls);
      } else {
        this.log(LOG_LEVEL.Debug, fname, `No new phone calls created for interactionId: ${interaction.interactionId}`, );
      }

      return newCalls;
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);
      throw err;
    } finally {
      this.log(LOG_LEVEL.Debug, fname, 'END <<<');
    }
  }

  private getExistingCallId(interaction: IInteraction, vendorCallKey: string): string | undefined {
    const fname = this.cname + 'getExistingCallId';
    try {
      this.log(LOG_LEVEL.Debug, fname, 'Checking if an existing call has the DEG callId for this interaction.', { interactionId: interaction.interactionId, vendorCallKey });
      const filteredParties = this.activeParties.filter(ap => ap.interactionId === interaction.interactionId);

      if (filteredParties.length === 0) {
        this.log(LOG_LEVEL.Trace, fname, 'No parties found for interactionId', { interactionId: interaction.interactionId });
        return;
      } else {
        this.log(LOG_LEVEL.Trace, fname, `Found ${filteredParties.length} parties for interactionId`, { interactionId: interaction.interactionId, parties: filteredParties });
        const initialParty = filteredParties.find(ap => ap.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER);
        if (initialParty) {
          const index = this.getPartyIndex(initialParty.phoneNumber);
          if (index === -1) {
            this.log(LOG_LEVEL.Trace, fname, 'No active party found for initial caller', { initialParty });
            return;
          } else {
            const activeCall = this.activeCalls[index];
            if (activeCall) {
              this.log(LOG_LEVEL.Trace, fname, 'Found active call for initial caller', { activeCall });
              return activeCall.callId;
            }
          }
        }
      }
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to get existing call ID.', error);
    }
    return;
  }

  protected getVendorCallKeyFromInteraction(interaction: IInteraction): string {
    const fname = 'getVendorCallKeyFromInteraction';
    try {
      this.log(LOG_LEVEL.Debug, fname, 'Getting vendor call key from interaction', { interactionId: interaction.interactionId });
      let vendorCallKey: string = this.communicationService.formatInteractionId(interaction.scenarioId ?? interaction.interactionId);
      if (this.scs.VendorCallKeyCAD) {
        if (!interaction.details?.fields[this.scs.VendorCallKeyCAD]?.Value) {
          this.log(LOG_LEVEL.Trace, fname, 'VendorCallKeyCAD is not present in the interaction, using interactionId instead', {
            interactionId: interaction.interactionId,
            vendorCallKey: this.scs.VendorCallKeyCAD,
            CAD: interaction.details?.fields
          });
        } else {
          this.log(LOG_LEVEL.Trace, fname, 'CAD with vendorCallKey found', { interactionId: interaction.interactionId, vendorCallKey });
          vendorCallKey = interaction.details.fields[this.scs.VendorCallKeyCAD].Value;
        }
      }
      this.log(LOG_LEVEL.Trace, fname, `Using VendorCallKey ${vendorCallKey} for Interaction: ${interaction.interactionId}`);
      return vendorCallKey;
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to get vendor call key from interaction', error);
      return '';
    }
  }

  private randomTag(numDigits: number = 3): string {
    return Math.floor(Math.random() * Math.pow(10, numDigits)).toString();
  }

  private rejectPromiseTimeout(promise: DeferredPromise, errorMessage?: Error | string): void {
    const fname = `${this.cname}rejectPromiseTimeout`;

    try {
      this.log(LOG_LEVEL.Debug, fname, `${promise.kind} timed out${ errorMessage != undefined ? ' with error message' : ' without error message'}.`, { promiseDetails: promise.additionalDetails, errorMessage });

      if (!promise.reject) {
        throw new Error('Deferred promise has null attributes!');
      }

      if (!errorMessage || typeof errorMessage == 'string') {
        promise.reject(new Error(errorMessage));

        // In case of failure, update calls
        if (promise.kind === 'Conference' || promise.kind === 'Hangup') {

          // eslint-disable-next-line @typescript-eslint/prefer-for-of
          for (let i = 0; i < this.activeCalls.length; i++) {
            if (this.activeCalls[i].callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER) {
              this.publishEventToScv({
                eventType: Constants.VOICE_EVENT_TYPE.CALL_CONNECTED,
                dataType: 'CallResult',
                data: new CallResult({
                  call: this.activeCalls[i]
                })
              }, 'reject-timeout');
            } else {
              this.publishEventToScv({
                eventType: Constants.VOICE_EVENT_TYPE.PARTICIPANT_CONNECTED,
                dataType: 'ParticipantResult',
                data: new ParticipantResult({
                  phoneNumber: this.activeCalls[i].phoneNumber,
                  initialCallHasEnded: false,
                  callInfo: this.activeCalls[i].callInfo,
                  callId: this.activeCalls[i].callId
                })
              }, 'reject-timeout');
            }
          }
        }
      } else {
        promise.reject(errorMessage);
      }
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);

      throw err;
    } finally {
      this.log(LOG_LEVEL.Debug, fname, 'END <<<');
    }
  }

  private removePromise(promise: DeferredPromise): void {
    clearTimeout(promise.timeoutHandle);
    this.log(LOG_LEVEL.Trace, 'Home.removePromise', 'Cleared promise rejection timer');

    const promiseIdx = this.promisesToResolve.findIndex(dp => dp.id === promise.id);

    if (promiseIdx >= 0) {
      this.promisesToResolve.splice(promiseIdx, 1);
    } else {
      this.log(LOG_LEVEL.Warning, 'Home.removePromise', 'Promise no longer exists in list', {
        id: promise.id,
        kind: promise.kind,
        timeCreated: promise.timeCreated,
        details: promise.additionalDetails
      });
    }
  }

  // Needs to return a Promise-type because of DaVinci interface
  // eslint-disable-next-line @typescript-eslint/require-await
  protected async receivePresenceFromFramework(
    presence: string,
    reason?: string,
    initiatingApp?: string
  ): Promise<void> {
    const cId = generateCID();
    const fname = this.cname + `receivePresenceFromFramework(${cId}):`;
    try {
      this.log(LOG_LEVEL.Debug, fname, 'Presence Change', { presence, reason, initiatingApp });

      const presenceEvent: IEventQueuePresence = {
        type: EventQueueType.PRESENCE,
        presence,
        processId: cId,
        reason,
        timestamp: Date.now(),
        initiatingApp: initiatingApp
      };

      if (this.eventQueue.length === 0 ) {
        this.log(LOG_LEVEL.Debug, fname, 'Queue is empty, pushing presence onto queue and starting processing.', { presenceEvent });
        this.eventQueue.push(presenceEvent);
        this.processEventsQueue();
      } else {
        this.log(LOG_LEVEL.Debug, fname, `${this.eventQueue.length} events already in queue, appending presence to the end.`, { presenceEvent });
        this.eventQueue.push(presenceEvent);
      }
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to set presence', error);
    }
  }

  protected async processPresenceFromFramework(
    cId: CID,
    presence: string,
    reason?: string,
    initiatingApp?: string,
  ): Promise<void> {
    const fname = this.cname + `processPresenceFromFramework(${cId}):`;

    try {
      this.log(LOG_LEVEL.Debug, fname, 'Processing Presence Change', { presence, reason, initiatingApp });

      if (!this.loggedIn && presence.toLowerCase() !== 'pending') {
        this.bridgeEventsService?.sendEvent(AMC_EVENT_NAMES.LOGIN);
        this.loggedIn = true;
        this.log(LOG_LEVEL.Debug, fname, 'Sending Login event to Salesforce');
      }

      this.currentPresence = presence;
      this.currentReason = reason;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const presencesToResolve = this.promisesToResolve.filter(dp => dp.kind === 'AgentStatus' && dp.pending) as PresenceDeferredPromise[];
      let matchedPromisePresence = false;

      if (presencesToResolve.length > 0) {
        this.log(LOG_LEVEL.Trace, fname, 'Unlocking all set-presence requests');

        for (const dp of presencesToResolve) {
          if (!dp.resolve) {
            this.log(LOG_LEVEL.Error, fname, 'Promise has no resolve function!');
          } else {
            if (
              presence === dp.additionalDetails.presence &&
              (reason ?? '') === (dp.additionalDetails.reason ?? '')
            ) {
              matchedPromisePresence = true;
              this.log(LOG_LEVEL.Trace, fname, 'Promise presence and reason matched', { promiseDetails: dp.additionalDetails });
              dp.resolve(new GenericResult({ success: true }));
              this.removePromise(dp);
            } else {
              this.log(LOG_LEVEL.Trace, fname, 'Promise presence and reason not matched', { promiseDetails: dp.additionalDetails });
            }
          }
        }
      }

      if (!matchedPromisePresence) {
        this.log(LOG_LEVEL.Trace, fname, 'No presence promises to resolve, sending presence to Salesforce', { presence, reason });
        // This event raises a presence change event that was not triggered by Salesforce
        let presenceId: string | undefined;
        if (reason != null && reason !== '') {
          presenceId = this.presencesFromSalesforce.get(
            this.scs.channelToSalesforceMap.get(`${presence}|${reason}`) || ''
          );
        } else {
          presenceId = this.presencesFromSalesforce.get(
            this.scs.channelToSalesforceMap.get(presence) || ''
          );
        }
        if (presenceId) {
          const event: ScvVoiceEventPayload = {
            eventType: Constants.SHARED_EVENT_TYPE.SET_AGENT_STATUS,
            dataType: 'AgentStatusInfo',
            data: new AgentStatusInfo({
              statusId: presenceId
            })
          };

          const agentStatusEchoPromise = new AgentStatusEchoDeferredPromise({ statusId: presenceId });

          this.promisesToResolve.push(agentStatusEchoPromise);

          this.publishEventToScv(event, 'presence');
          this.startPromiseRejectionTimer(
            agentStatusEchoPromise,
            'Agent status echo wait period is over'
          );
          try {
            await agentStatusEchoPromise.promise;
          } catch {}

        } else {
          this.log(LOG_LEVEL.Warning, fname, 'Presence not found in map', { presence, reason });
        }
      }
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to set presence', error);
      throw error;
    } finally {
      this.log(LOG_LEVEL.Debug, fname, 'Finished Processing Presence Change', { presence, reason, initiatingApp });
    }
  }

  protected publishActiveCallsToSalesforce(): void {
    const fname = this.cname + 'publishActiveCallsToSalesforce';
    try {
      this.log(LOG_LEVEL.Trace, fname, 'Publishing active calls to Salesforce');

      for (const call of this.activeCalls) {
        if (call.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER && call.state === Constants.CALL_STATE.CONNECTED) {
          this.publishEventToScv({
            eventType: Constants.VOICE_EVENT_TYPE.CALL_CONNECTED,
            dataType: 'CallResult',
            data: new CallResult({
              call
            })
          });
        } else {
          this.publishEventToScv({
            eventType: Constants.VOICE_EVENT_TYPE.PARTICIPANT_CONNECTED,
            dataType: 'ParticipantResult',
            data: new ParticipantResult({
              initialCallHasEnded: this.activeCalls.some(ac => ac.callAttributes.participantType === Constants.PARTICIPANT_TYPE.INITIAL_CALLER && ac.state === Constants.CALL_STATE.ENDED),
              callId: call.callId,
              callInfo: call.callInfo,
              phoneNumber: call.phoneNumber
            })
          });
        }
      }
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to publish active calls to Salesforce', error);
    } finally {
      this.storeCallsAndPartiesToLocalStorage();
    }
  }

  // Needs to return Promise-type due to DaVinci API
  // eslint-disable-next-line @typescript-eslint/require-await
  async getActiveCalls(): Promise<PhoneCall[]> {
    const functionName = 'Home.' + 'getActiveCalls';
    try {
      this.log(LOG_LEVEL.Trace, functionName, `Getting Active ${this.activeCallsCopy.length} Calls`, this.activeCallsCopy);

      this.getActiveCallsCounter++;
      this.log(LOG_LEVEL.Critical, functionName, `getActiveCallsCounter = ${this.getActiveCallsCounter}`);
      // if (this.getActiveCallsCounter <= 2) {
      //   this.log(LOG_LEVEL.Critical, functionName, `Returning nothing for the first 2 get active calls`);
      //   return Promise.resolve([]);
      // } else {
      return Promise.resolve(this.activeCallsCopy);
      // }
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Failed to get active calls', error);
      throw error;
    }
  }

  // Needs to return Promise-type due to DaVinci API
  // eslint-disable-next-line @typescript-eslint/require-await
  async getAgentStatus(): Promise<string> {
    const functionName = 'Home.' + 'getAgentStatus';
    try {
      this.log(LOG_LEVEL.Trace, functionName, 'Getting Agent Status', this.state.agentStatus);
      return this.state.agentStatus;
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Failed to get agent status', error);
      throw new Error('getAgentStatus failed!');
    }
  }

  /**
   ** This function is responsible for logging out the user when an event is received from DaVinci and for resolving a logout promise if it exists.
   *
   * @private
   * @param {string} [reason]
   * @memberof HomeBYOTComponent
   */
  private async logoutCallback(reason?: string): Promise<void> {
    const fname = 'Home.' + 'logoutCallback';
    try {
      this.log(LOG_LEVEL.Debug, fname, 'Processing Logout Event received from DaVinci Framework.', reason);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const logoutPromises = this.promisesToResolve.filter(dp => dp.kind === 'Logout') as LogoutDeferredPromise[];

      if (logoutPromises.length > 0) {
        for (const lp of logoutPromises) {
          if (!lp.resolve) {
            this.log(LOG_LEVEL.Warning, fname, 'Logout promise has no resolve function!');
          } else {
            lp.resolve(reason);
            this.removePromise(lp);
          }
        }
      } else {
        this.bridgeEventsService?.sendEvent(AMC_EVENT_NAMES.LOGOUT);
      }
      await this.loggerService.logger.pushLogsAsync();
      this.removeLocalStorageOnLogout();
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to logout', error);
    }
  }

  /**
   ** This function raises a logout event to DaVinci and creates a promise to resolve when the logout event is processed by DaVinci.
   *
   * @return {*}
   * @memberof HomeBYOTComponent
   */
  // eslint-disable-next-line @typescript-eslint/promise-function-async
  logoutUser(): Promise<string | undefined> {
    const functionName = 'Home.' + 'logoutUser';
    try {
      // Toodo: When the agent is set to offline in the softphone the agent needs to be signed out of agent
      this.log(LOG_LEVEL.Debug, functionName, 'Logging out user');

      const logoutPromise = new LogoutDeferredPromise();

      this.promisesToResolve.push(logoutPromise);
      this.startPromiseRejectionTimer(logoutPromise, 'Failed to logout');
      this.loggedIn = false;
      logout();

      return logoutPromise.promise;
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Failed to logout user', error);

      return Promise.resolve(undefined);
    }
  }

  private startPromiseRejectionTimer(promise: DeferredPromise, errorMessage?: Error | string): void {
    clearTimeout(promise.timeoutHandle);

    promise.timeoutHandle = setTimeout(() => {
      if (!promise.pending) {
        this.removePromise(promise);
        return;
      }

      this.rejectPromiseTimeout(promise, errorMessage);
      this.removePromise(promise);
    }, this.scs.promiseTimeouts[promise.kind]);
  }

  async onPresenceChangeFromScv(
    agentPresence?: Partial<IAgentStatusPayload>
  ): Promise<any> {
    const functionName = `${this.cname}onPresenceChangeFromScv`;
    try {
      this.log(LOG_LEVEL.Debug, functionName, 'Presence Change Event from Salesforce', agentPresence);

      if (
        agentPresence?.agentStatus == Constants.AGENT_STATUS.OFFLINE &&
        agentPresence?.agentStatusInfo?.reasonForChange == 'OMNICHANNEL_LOGOUT'
      ) {
        this.log(LOG_LEVEL.Debug, functionName, 'Presence set to Offline with reasonForChange as OMNICHANNEL_LOGOUT, logging user out.', agentPresence.agentStatusInfo.reasonForChange);
        return await this.logoutUser();
      }

      if (!this.scs.salesforceToChannelMap) {
        const message = 'SCV -> DaVinci presence map is undefined.';

        this.log(LOG_LEVEL.Error, functionName, message);
        throw new Error(`${functionName}: ${message}`);
      }

      if (
        !agentPresence?.agentStatus ||
        !agentPresence.agentStatusInfo?.statusApiName ||
        !agentPresence.agentStatusInfo.statusId ||
        !agentPresence.agentStatusInfo.statusName
      ) {
        if (agentPresence?.agentStatus == Constants.AGENT_STATUS.OFFLINE) {
          this.log(LOG_LEVEL.Warning, functionName, 'Offline Malformed presence, assuming the user is in the log in process.');
          const daVinciPresence: IGetPresenceResult = await getPresence();
          const status: AgentStatusInfo = this.getSalesforceStatusFromDaVinciPresence(daVinciPresence);
          this.log(LOG_LEVEL.Trace, functionName, 'Sending Salesforce status', status);
          return status;
        }

        const message = 'Agent Status payload is malformed';

        this.log(LOG_LEVEL.Error, functionName, message, agentPresence);

        throw new Error(message);
      }

      const pending = Boolean(agentPresence.enqueueNextState);
      if (pending) {
        agentPresence.agentStatusInfo.statusName =
          'Pending ' + agentPresence.agentStatusInfo.statusName;
      }

      const presenceMapped = this.scs.salesforceToChannelMap[agentPresence.agentStatusInfo.statusName];

      if (!presenceMapped) {
        this.log(LOG_LEVEL.Warning, functionName, `Presence not in map (status=${agentPresence.agentStatus} | name=${agentPresence.agentStatusInfo.statusName}`, agentPresence);
        throw new Error(`Presence not in map (status=${agentPresence.agentStatus} | name=${agentPresence.agentStatusInfo.statusName}`);
      }
      this.log(LOG_LEVEL.Trace, functionName, 'Mapped Presence', presenceMapped);

      const presenceArray = presenceMapped.split('|');
      const presence = presenceArray[0];
      let reason = '';

      if (presenceArray.length > 1) {
        // This will split a presence if it maps to a reason code using the | character
        reason = presenceArray[1];
        this.log(LOG_LEVEL.Trace, functionName, 'Mapped Reason', reason);
      }

      const incomingStatusId = agentPresence?.agentStatusInfo?.statusId;
      const matchingAgentStatusEchoPromise = this.promisesToResolve.find(p =>
        p.kind === 'AgentStatusEcho' &&
        p instanceof AgentStatusEchoDeferredPromise &&
        (p as AgentStatusEchoDeferredPromise).additionalDetails.statusId === incomingStatusId
      );

      if (matchingAgentStatusEchoPromise) {
        this.log(LOG_LEVEL.Trace, functionName, 'Resolving unsolicited presence echo', {
          statusId: incomingStatusId,
          promiseId: matchingAgentStatusEchoPromise.id
        });
        const typedPromise = matchingAgentStatusEchoPromise as AgentStatusEchoDeferredPromise;
        typedPromise.resolve?.(new GenericResult({ success: true }));
        this.removePromise(typedPromise);
        return new GenericResult({ success: true });
      }


      this.log(LOG_LEVEL.Trace, functionName, 'Current Presence', {
        currentPresence: this.currentPresence,
        currentReason: this.currentReason,
      });
      this.log(LOG_LEVEL.Debug, functionName, 'Presence', { presence, reason, pending });
      if (this.currentPresence === presence && this.currentReason === reason) {
        this.log(LOG_LEVEL.Warning, functionName, 'Agent is already in this presence', { presence, reason, pending });
        return presence;
      }
      this.log(LOG_LEVEL.Trace, functionName, 'Calling ClickToAct() to SETPRESENCE call..', { presence, reason, pending });
      const presencePromise = new PresenceDeferredPromise({ presence: presence, reason: reason, pending: pending });

      this.promisesToResolve.push(presencePromise);
      clickToAct(
        '',
        undefined,
        CHANNEL_TYPES.Telephony,
        CONTEXTUAL_OPERATION_TYPE.SetPresence,
        '',
        { presence: presence, reason: reason, pending: pending }
      );
      this.startPromiseRejectionTimer(presencePromise);
      this.log(LOG_LEVEL.Trace, functionName, 'Added to Promise to resolve for presence');
      return presencePromise.promise;
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Failed to change presence', error);
      throw new Error('Failed to set presence');
    }
  }

  private getSalesforceStatusFromDaVinciPresence(daVinciPresence: IGetPresenceResult): AgentStatusInfo {
    const fname = 'getSalesforceStatusFromDaVinciPresence';
    try {
      this.log(LOG_LEVEL.Trace, fname, 'Presence from DaVinci.', daVinciPresence);

      let presenceId: string | undefined = this.presencesFromSalesforce.get(
        this.scs.channelToSalesforceMap.get(daVinciPresence.presence) || ''
      );

      if (daVinciPresence.reason != null && daVinciPresence.reason !== '') {
        presenceId = this.presencesFromSalesforce.get(
          this.scs.channelToSalesforceMap.get(`${daVinciPresence.presence}|${daVinciPresence.reason}`) || ''
        );
      }

      if (presenceId) {
        this.log(LOG_LEVEL.Trace, fname, 'Found Salesforce status', presenceId);
        return new AgentStatusInfo({ statusId: presenceId });
      } else {
        throw new Error('Failed to get presence.');
      }
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to parse presence.', error);
      throw error;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getPhoneContacts(data: IGetContactsPayload): Promise<Contact[]> {
    const fname = `${this.cname}.getPhoneContacts`;
    let contacts: Contact[] = [];

    try {
      if (this.scs.enableContextualContacts) {
        const contextualContacts: IContextualContact[] = await getLiveAgents();
        this.log(LOG_LEVEL.Trace, fname, `${contextualContacts.length} contextual contacts found.`, contextualContacts);
        const liveContacts: Contact[] = contextualContacts
          .filter((contact) => {
            if (!contact.channels) {
              return false;
            }

            const contactChannels = contact?.channels;

            if (!Array.isArray(contactChannels)) {
              throw new Error(
                'getPhoneContacts: channels on [contact] is not an array'
              );
            }

            const voiceChannels = contactChannels.filter(
              (channel) => channel?.channelType === CHANNEL_TYPES.Telephony
            );
            // It is supposed to be just one voice channel
            if (voiceChannels.length !== 1) {
              return false;
            }
            if (!voiceChannels[0].id) {
              return false;
            }
            return true;
          })
          .map((item) => {
            const itemChannels = item?.channels;

            if (!Array.isArray(itemChannels)) {
              throw new Error('getPhoneContacts: itemChannels is not an array.');
            }

            const telephoneChannel = itemChannels.find(
              (channel) => channel.channelType === CHANNEL_TYPES.Telephony
            );
            return new Contact({
              id: item.uniqueId,
              type: Constants.CONTACT_TYPE.AGENT,
              name: item.firstName + ' ' + item.lastName,
              phoneNumber: telephoneChannel!.id,
              availability:
              item.presence === 'Ready'
                ? Constants.AGENT_AVAILABILITY.AVAILABLE.toString()
                : Constants.AGENT_AVAILABILITY.BUSY.toString(),
            });
          });

        contacts.push(...liveContacts);
      }

      contacts.push(...this.scs.contactList);

      contacts = this.filterContacts(contacts);

      contacts = this.sortContactsByName(contacts);

      contacts = this.sortContacts(contacts);

      this.log(LOG_LEVEL.Debug, fname, `Final filtered contacts list with ${contacts.length} contacts.`, contacts);
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to get live agents', error);
    } finally {
      return contacts;
    }
  }

  private filterContacts(contacts: Contact[]): Contact[] {
    const fname = `${this.cname}.filterContacts`;
    try {
      this.log(LOG_LEVEL.Trace, fname, 'Contacts before filtering.', contacts);
      if (!this.scs.enableQueues) {
        contacts = contacts.filter(c => c.type !== Constants.CONTACT_TYPE.QUEUE);
      }
      if (!this.scs.enableAgents) {
        contacts = contacts.filter(c => c.type !== Constants.CONTACT_TYPE.AGENT);
      }
      if (!this.scs.enablePhoneNumbers) {
        contacts = contacts.filter(c => c.type !== Constants.CONTACT_TYPE.PHONENUMBER);
      }
      if (!this.scs.enablePhoneBooks) {
        contacts = contacts.filter(c => c.type !== Constants.CONTACT_TYPE.PHONEBOOK);
      }
      if (!this.scs.enableFlows) {
        contacts = contacts.filter(c => c.type !== Constants.CONTACT_TYPE.FLOW);
      }
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to filter contacts.');
    } finally {
      this.log(LOG_LEVEL.Trace, fname, 'Contacts after filtering.', contacts);
      return contacts;
    }
  }

  private sortContactsByName(contacts: Contact[]): Contact[] {
    const fname = `${this.cname}.sortContactsByName`;
    try {
      this.log(LOG_LEVEL.Trace, fname, `List of ${contacts.length} unsorted contacts`, contacts);

      contacts.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });

    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to sort contacts by name.', error);
    } finally {
      this.log(LOG_LEVEL.Trace, fname, `List of ${contacts.length} sorted contacts`, contacts);
      return contacts;
    }
  }

  private sortContacts(contacts: Contact[]): Contact[] {
    const fname = `${this.cname}.sortContacts`;
    try {
      this.log(LOG_LEVEL.Trace, fname, `List of ${contacts.length} unsorted contacts`, contacts);
      // this.scs.typeSortingOrder should be an array of Constants.CONTACT_TYPE
      const typeOrder: Constants.CONTACT_TYPE[] = this.scs.typeSortingOrder || [];

      contacts.sort((a, b) => {
        const aIndex = typeOrder.indexOf(a.type);
        const bIndex = typeOrder.indexOf(b.type);
        // If not found, put at the end
        return (aIndex === -1 ? typeOrder.length : aIndex) - (bIndex === -1 ? typeOrder.length : bIndex);
      });
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to sort contacts.', error);
    } finally {
      this.log(LOG_LEVEL.Trace, fname, `List of ${contacts.length} sorted contacts`, contacts);
      return contacts;
    }
  }

  /*
   *  Callback functions for the bridge that handle call controls
   */
  // eslint-disable-next-line @typescript-eslint/promise-function-async
  acceptCallEvent(call: Pick<PhoneCall, 'callId'>): Promise<CallResult> {
    const fname = 'Home.' + 'acceptCallEvent';
    try {
      this.log(LOG_LEVEL.Debug, fname, 'START ');

      const existingCall = this.activeCalls.find(phoneCall => phoneCall.callId === call.callId);
      if (!existingCall) {
        throw new Error('Call does not exist in active calls list.');
      }

      this.log(LOG_LEVEL.Information, fname, 'Calling ClickToAct() to ACCEPT call..', call);

      const eventPromise = new AcceptRejectDeferredPromise({
        callId: existingCall.callId
      }, true);
      this.promisesToResolve.push(eventPromise);
      this.log(LOG_LEVEL.Trace, fname, 'Added to promises to resolve', this.promisesToResolve);
      clickToAct(
        existingCall.phoneNumber,
        undefined,
        CHANNEL_TYPES.Telephony,
        CONTEXTUAL_OPERATION_TYPE.Answer,
        this.activeParties[this.getPartyIndex(existingCall.contact.phoneNumber)].interactionId,
        undefined
      );
      this.startPromiseRejectionTimer(eventPromise, new Error('Failed to accept call'));
      return eventPromise.promise;
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to accept call', error);

      throw error;
    }
  }

  // eslint-disable-next-line @typescript-eslint/promise-function-async
  rejectCallEvent(call: Pick<PhoneCall, 'callId'>): Promise<CallResult> {
    const fname = this.cname + 'rejectCallEvent';

    try {
      this.log(LOG_LEVEL.Debug, fname, 'START >>>');
      this.log(LOG_LEVEL.Trace, fname, `${this.activeCalls.length} calls at the time of rejection\n${this.promisesToResolve.length} pending promises\nPresence: ${this.currentPresence}\nReason: ${this.currentReason}`, {
        presence: this.currentPresence,
        reason: this.currentReason,
        activeCalls: this.activeCalls,
        activeParties: this.activeParties,
        eventQueue: this.eventQueue,
        call: call
      });

      // ToDo: Implement logic for handling a reject if the call from Salesforce doesn't exist
      const existingCall = this.getCall(call?.callId);
      if (!existingCall) {
        this.log(LOG_LEVEL.Error, fname, 'Call does not exist in active calls list.');
        return Promise.resolve(new CallResult({
          call: new PhoneCall({ callId: call.callId })
        }));
      }

      this.log(LOG_LEVEL.Information, fname, 'Calling C2A to REJECT call..', call);

      const eventPromise = new AcceptRejectDeferredPromise({
        callId: call.callId,
      }, false);
      this.promisesToResolve.push(eventPromise);
      this.log(LOG_LEVEL.Trace, fname, 'Added to promises to resolve', this.promisesToResolve);
      clickToAct(
        existingCall.phoneNumber,
        undefined,
        CHANNEL_TYPES.Telephony,
        CONTEXTUAL_OPERATION_TYPE.Reject,
        this.activeParties[this.getPartyIndex(existingCall.contact.phoneNumber)].interactionId,
        undefined
      );
      this.startPromiseRejectionTimer(eventPromise, new Error('Failed to reject call'));
      return eventPromise.promise;
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);
      throw err;
    } finally {
      this.log(LOG_LEVEL.Debug, fname, 'END <<<');
    }
  }

  async hangupOrCompleteWarmTransfer(data: IEndCallPayload): Promise<HangupResult> {
    const fname = this.cname + 'hangupOrCompleteWarmTransfer';
    try {
      this.log(LOG_LEVEL.Debug, fname, 'Hangup Call Event', data.call);

      const isConference = this.isInConference();
      const isWarmTransferComplete = this.isWarmTransfer(data);
      this.log(LOG_LEVEL.Critical, fname, `Call ${isConference ? 'is a conference' : 'is not a conference'} and ${isWarmTransferComplete ? 'is a warm transfer' : 'is not a warm transfer'}`);

      const storedCalls: AmcCall[] = [];

      if (data.call.callId) {
        storedCalls.push(...this.activeCalls.filter(ac => isWarmTransferComplete || ac.callId === data.call.callId));
      } else if (data.call.callAttributes?.participantType) {
        this.log(LOG_LEVEL.Warning, fname, 'Call has no call ID! Relying on participant type', data);

        storedCalls.push(...this.activeCalls.filter(ac =>
          isWarmTransferComplete ||
          data.call.callAttributes?.participantType == Constants.PARTICIPANT_TYPE.AGENT ||
          ac.callAttributes.participantType === data.call.callAttributes?.participantType
        ));

        if (storedCalls.length > 1 && isConference && !isWarmTransferComplete) {
          // if in a conference dropping the first leg should be all that is necessary
          storedCalls.splice(1);
        } else if (storedCalls.length > 2) {
          // Remove all but the first two entries from storedCalls
          storedCalls.splice(2);
        }
      }

      if (storedCalls.length > 1 && !isConference && isWarmTransferComplete && !this.scs.warmTransferEnabled) {
        const connectedConsultCall = storedCalls.find(sc => !sc.callAttributes.isOnHold);

        if (connectedConsultCall) {
          storedCalls.splice(0, storedCalls.length, connectedConsultCall);
        }
      }

      if (!storedCalls.length) {
        throw new Error('Could not find an active call to end.');
      }

      const hangupPromises: HangupDeferredPromise[] = [];

      for (const sc of storedCalls) {
        hangupPromises.push(new HangupDeferredPromise({
          callId: sc.callId,
          raiseAsUnsolicited: false
        }));
      }

      this.promisesToResolve.push(...hangupPromises);

      this.log(LOG_LEVEL.Trace, fname, 'Added to promises to resolve', this.promisesToResolve);

      if (isWarmTransferComplete) {
        // TODO: This needs to change to being the last call in storedCalls not hard coded to the second one

        if (this.scs.warmTransferEnabled) {
          let activePartyIdx = this.getPartyIndex(storedCalls[1].contact.phoneNumber);
          if (activePartyIdx === -1 && data.call.callId) {
            // assuming that the active call is the correct index
            activePartyIdx = this.getCallIndexUsingCallId(data.call.callId);
          }
          this.log(LOG_LEVEL.Information, fname, 'Calling C2A for Warm Transfer Complete', [
            storedCalls[1].phoneNumber,
            undefined,
            CHANNEL_TYPES.Telephony,
            CONTEXTUAL_OPERATION_TYPE.WarmTransfer,
            this.activeParties[activePartyIdx].interactionId,
            activePartyIdx.toString()
          ]);
          clickToAct(
            storedCalls[1].phoneNumber,
            undefined,
            CHANNEL_TYPES.Telephony,
            CONTEXTUAL_OPERATION_TYPE.WarmTransfer,
            this.activeParties[activePartyIdx].interactionId,
            activePartyIdx.toString()
          );
        } else {
          let activePartyIdx = this.getPartyIndex(storedCalls[0].contact.phoneNumber);
          if (activePartyIdx === -1 && data.call.callId) {
            // assuming that the active call is the correct index
            activePartyIdx = this.getCallIndexUsingCallId(data.call.callId);
          }
          this.log(LOG_LEVEL.Trace, fname, 'Warm transfer disabled, dropping 3rd party.', [
            storedCalls[0].phoneNumber,
            undefined,
            CHANNEL_TYPES.Telephony,
            CONTEXTUAL_OPERATION_TYPE.Hangup,
            this.activeParties[activePartyIdx].interactionId,
            activePartyIdx.toString()
          ]);
          clickToAct(
            storedCalls[0].phoneNumber,
            undefined,
            CHANNEL_TYPES.Telephony,
            CONTEXTUAL_OPERATION_TYPE.Hangup,
            this.activeParties[activePartyIdx].interactionId,
            activePartyIdx.toString()
          );
        }

        hangupPromises.forEach(hp => {
          this.startPromiseRejectionTimer(hp, 'Failed to complete warm transfer');
        });
      } else {
        const seenInteractionIds: Record<string, boolean | undefined> = {};
        for (const hp of hangupPromises) {

          this.startPromiseRejectionTimer(hp, 'Failed to end call');

          const curCall = this.getCall(hp.additionalDetails.callId);
          if (!curCall) {
            throw new Error('Could not find active call');
          }

          let davinciPartyIdx = curCall.parties.findIndex(p => p === curCall.contact.phoneNumber);
          if (davinciPartyIdx < 0) {
            davinciPartyIdx = 0;
          }

          let activePartyIdx = this.getPartyIndex(curCall.contact.phoneNumber);
          if (activePartyIdx === -1 && data.call.callId) {
            // assuming that the active call is the correct index
            activePartyIdx = this.getCallIndexUsingCallId(data.call.callId);
          }

          const interactionId = this.activeParties[activePartyIdx].interactionId;

          const isConferenceParticipantDrop = isConference && data.call.callAttributes?.participantType !== Constants.PARTICIPANT_TYPE.AGENT;

          if (seenInteractionIds[interactionId] && !isConferenceParticipantDrop) {
            continue;
          }
          this.log(LOG_LEVEL.Information, fname, 'Calling C2A for hangup', [
            curCall.phoneNumber,
            undefined,
            CHANNEL_TYPES.Telephony,
            isConferenceParticipantDrop ? CONTEXTUAL_OPERATION_TYPE.RemoveParticipant : CONTEXTUAL_OPERATION_TYPE.Hangup,
            interactionId,
            isConferenceParticipantDrop ? davinciPartyIdx.toString() : undefined
          ]);
          clickToAct(
            curCall.phoneNumber,
            undefined,
            CHANNEL_TYPES.Telephony,
            isConferenceParticipantDrop ? CONTEXTUAL_OPERATION_TYPE.RemoveParticipant : CONTEXTUAL_OPERATION_TYPE.Hangup,
            interactionId,
            isConferenceParticipantDrop ? davinciPartyIdx.toString() : undefined
          );

          seenInteractionIds[interactionId] = true;
        }
      }
      // eslint-disable-next-line @typescript-eslint/promise-function-async
      const results: HangupResult[] = await Promise.all(hangupPromises.map(hp => hp.promise));

      this.log(LOG_LEVEL.Trace, fname, 'Hangup results', results);

      const calls: PhoneCall[] = [];

      results.forEach(hr => {
        const tempCalls: PhoneCall[] = [];
        hr.calls.forEach(call => {
          this.log(LOG_LEVEL.Trace, fname, `Call is a PhoneCall? ${call instanceof PhoneCall}`, call);
          this.log(LOG_LEVEL.Trace, fname, `Call is a AmcCall? ${call instanceof AmcCall}`, call);
          const saturatedCall = saturatePhoneCall(call);
          this.log(LOG_LEVEL.Trace, fname, 'Saturated Call', saturatedCall);
          tempCalls.push(new PhoneCall(call));
        });
        this.log(LOG_LEVEL.Trace, fname, 'Adding calls from hangup result', tempCalls);
        calls.push(...tempCalls);
      });

      for (const call of calls) {
        this.log(LOG_LEVEL.Warning, fname, `Responding with hungup call: ${call.callId}`, call);
      }

      const hangup =  new HangupResult({
        calls
      });

      this.log(LOG_LEVEL.Trace, fname, 'Hangup Result', hangup);

      return hangup;
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to hangup call', error);

      throw error;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async muteCallEvent(call: Partial<PhoneCall>): Promise<MuteToggleResult> {
    throw new Error('not implemented');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async unmuteCallEvent(call: Partial<PhoneCall>): Promise<MuteToggleResult> {
    throw new Error('not implemented');
  }

  // eslint-disable-next-line @typescript-eslint/promise-function-async
  holdCallEvent(call: Partial<Pick<PhoneCall, 'callId'> & { callAttributes: Pick<PhoneCallAttributes, 'participantType'> }>): Promise<HoldToggleResult> {
    const functionName = 'Home.' + 'holdCallEvent';
    try {
      this.log(LOG_LEVEL.Debug, functionName, 'Hold Call Event', call);

      this.log(
        LOG_LEVEL.Trace,
        functionName,
        'Calling ClickToAct() to HOLD call..',
        call
      );

      let storedCall: AmcCall | undefined;

      if (call.callId) {
        storedCall = this.activeCalls.find(ac => ac.callId === call.callId);
      }

      if (!storedCall && call.callAttributes?.participantType != null) {
        storedCall = this.activeCalls.find(ac => ac.callAttributes.participantType === call.callAttributes?.participantType);
      }

      if (!storedCall) {
        throw new Error('Could not find a matching phonecall for hold operation');
      }

      const holdPromise = new HoldToggleDeferredPromise({ callId: storedCall.callId });

      this.promisesToResolve.push(holdPromise);
      this.log(
        LOG_LEVEL.Trace,
        functionName,
        'Added to promises to resolve',
        this.promisesToResolve
      );
      this.startPromiseRejectionTimer(holdPromise, 'Failed to hold / unhold call');
      clickToAct(
        storedCall.phoneNumber,
        undefined,
        CHANNEL_TYPES.Telephony,
        CONTEXTUAL_OPERATION_TYPE.Hold,
        this.activeParties[this.getPartyIndex(storedCall.contact.phoneNumber)].interactionId,
        undefined
      );
      return holdPromise.promise;
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Failed to hold call', error);

      throw error;
    }
  }

  // eslint-disable-next-line @typescript-eslint/promise-function-async
  unholdCallEvent(call: Partial<Pick<PhoneCall, 'callId'> & { callAttributes: Pick<PhoneCallAttributes, 'participantType'> }>): Promise<HoldToggleResult> {
    const functionName = 'Home.' + 'holdCallEvent';
    try {
      this.log(LOG_LEVEL.Debug, functionName, 'Unhold Call Event', call);

      this.log(
        LOG_LEVEL.Trace,
        functionName,
        'Calling ClickToAct() to UNHOLD call..',
        call
      );

      let storedCall: AmcCall | undefined;

      if (call.callId) {
        storedCall = this.activeCalls.find(ac => ac.callId === call.callId);
      }

      if (!storedCall && call.callAttributes?.participantType != null) {
        storedCall = this.activeCalls.find(ac => ac.callAttributes.participantType === call.callAttributes?.participantType);
      }

      if (!storedCall) {
        throw new Error('Could not find a matching phonecall for hold operation');
      }

      const holdPromise = new HoldToggleDeferredPromise({ callId: storedCall.callId });

      this.promisesToResolve.push(holdPromise);
      this.log(
        LOG_LEVEL.Trace,
        functionName,
        'Added to promises to resolve',
        this.promisesToResolve
      );
      this.startPromiseRejectionTimer(holdPromise, 'Failed to hold / unhold call');
      clickToAct(
        storedCall.phoneNumber,
        undefined,
        CHANNEL_TYPES.Telephony,
        CONTEXTUAL_OPERATION_TYPE.Unhold,
        this.activeParties[this.getPartyIndex(storedCall.contact.phoneNumber)].interactionId,
        undefined
      );
      return holdPromise.promise;
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Failed to hold call', error);

      throw error;
    }
  }

  // eslint-disable-next-line @typescript-eslint/promise-function-async
  addParticipant(data: IAddParticipantPayload): Promise<ParticipantResult> {
    const functionName = 'Home.' + 'addParticipant';
    try {
      // Todo: implement PhoneNumber Formatting here
      this.log(
        LOG_LEVEL.Debug,
        functionName,
        'Calling ClickToAct() to ADDPARTICIPANT call..',
        { contact: data.contact, call: data.call }
      );

      const existingCall = this.getCall(data.call.callId);
      if (!existingCall) {
        throw new Error('Could not find active call');
      }

      const partyPromise = new ParticipantDeferredPromise({
        call: existingCall,
        contact: data.contact,
        isBlindTransfer: false
      });

      this.promisesToResolve.push(partyPromise);
      this.log(
        LOG_LEVEL.Trace,
        functionName,
        'Added to promises to resolve',
        this.promisesToResolve
      );
      clickToAct(
        data.contact.phoneNumber,
        undefined,
        CHANNEL_TYPES.Telephony,
        CONTEXTUAL_OPERATION_TYPE.AddParticipant,
        this.activeParties[this.getPartyIndex(existingCall.contact.phoneNumber)].interactionId,
        undefined
      );
      this.startPromiseRejectionTimer(partyPromise, 'Failed to add participant: ' + data.contact.phoneNumber);
      const result = partyPromise.promise;
      return result;
    } catch (error) {
      this.log(
        LOG_LEVEL.Error,
        functionName,
        'Failed to add participant',
        error
      );
      throw error;
    }
  }

  // eslint-disable-next-line @typescript-eslint/promise-function-async
  blindTransfer(data: IAddParticipantPayload): Promise<ParticipantResult> {
    const functionName = 'Home.' + 'blindTransfer';
    try {
      // TOoDO: implement PhoneNumber Formatting here
      this.log(
        LOG_LEVEL.Debug,
        functionName,
        'Calling ClickToAct() to BLINDTRANSFER call..',
        { contact: data.contact, call: data.call }
      );
      // TOoDO: Send transfer to Salesforce Via communicationService after successful transfer

      const partyPromise = new BlindTransferDeferredPromise({
        call: data.call,
        contact: data.contact,
      });

      this.promisesToResolve.push(partyPromise);
      this.log(
        LOG_LEVEL.Trace,
        functionName,
        'Added to promises to resolve',
        this.promisesToResolve
      );
      clickToAct(
        data.contact.phoneNumber,
        undefined,
        CHANNEL_TYPES.Telephony,
        CONTEXTUAL_OPERATION_TYPE.BlindTransfer,
        this.activeParties[this.getPartyIndex(this.getCall(data.call.callId)?.contact.phoneNumber)].interactionId,
        undefined
      );
      this.startPromiseRejectionTimer(partyPromise, 'Failed to perform blind transfer');
      return partyPromise.promise;
    } catch (error) {
      this.log(
        LOG_LEVEL.Error,
        functionName,
        'Failed to blind transfer',
        error
      );
      throw error;
    }
  }

  async conference(calls: Pick<PhoneCall, 'callId'>[]): Promise<HoldToggleResult> {
    const fname = 'Home.' + 'conference';
    try {
      // Todo: implement PhoneNumber Formatting here
      this.log(
        LOG_LEVEL.Debug,
        fname,
        'Calling ClickToAct() to conference call..',
        calls
      );

      if (this.activeCalls.length <= 1) {
        throw new Error('Not enough active calls to perform a merge');
      }

      this.log(
        LOG_LEVEL.Trace,
        fname,
        'Added to promises to resolve',
        this.promisesToResolve
      );

      // Await disconnects for all calls except the INITIAL_CALLER, which should
      // come in as a connected event in a merged state
      const newPromises: DeferredPromise[] = [];

      const conferencePromise = new ConferenceDeferredPromise({
        calls: this.activeCalls
      });

      this.promisesToResolve.push(...newPromises);
      this.promisesToResolve.push(conferencePromise);
      this.startPromiseRejectionTimer(conferencePromise);
      newPromises.forEach(np => {
        this.startPromiseRejectionTimer(np);
      });

      let activePartyIdx = this.getPartyIndex(this.activeCalls[1].contact.phoneNumber);
      if (activePartyIdx === -1) {
        // fallback to the second call as the target of the completed conference
        activePartyIdx = 1;
      }

      clickToAct(
        this.activeCalls[1].phoneNumber,
        undefined,
        CHANNEL_TYPES.Telephony,
        CONTEXTUAL_OPERATION_TYPE.Conference,
        this.activeParties[activePartyIdx].interactionId,
        undefined
      );

      // eslint-disable-next-line @typescript-eslint/promise-function-async
      await Promise.all(newPromises.map(hp => hp.promise));
      return conferencePromise.promise;
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, 'Failed to conference', error);
      throw error;
    }
  }

  // eslint-disable-next-line @typescript-eslint/promise-function-async
  sendOutbound(contact: Contact): Promise<CallResult> {
    const fname = this.cname + 'sendOutbound';

    try {
      this.log(LOG_LEVEL.Debug, fname, 'START >>>');

      this.log(LOG_LEVEL.Debug, fname, 'START ');

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!contact?.phoneNumber) {
        throw new Error('Contact object is null or has no phone number');
      }

      const formattedNumber = this.clickToDialFormatPhoneNumber(contact.phoneNumber).outputNumber;

      // ToDo: Determine if this needs to use the formattedNumber or the original number
      const eventPromise = new OutboundDialDeferredPromise({
        phoneNumber: contact.phoneNumber
      });
      this.promisesToResolve.push(eventPromise);
      this.log(LOG_LEVEL.Trace, fname, 'Added to promises to resolve', this.promisesToResolve);
      clickToDial(formattedNumber);
      this.startPromiseRejectionTimer(eventPromise, new Error('Failed to accept call'));
      return eventPromise.promise;
    } catch (err) {
      this.log(LOG_LEVEL.Error, fname, 'Error: ', err);

      throw err;
    } finally {
      this.log(LOG_LEVEL.Debug, fname, 'END <<<');
    }
  }

  async sendDigits(data: { digits: string } ): Promise<void> {
    const functionName = 'Home.' + 'sendDigits';
    try {
      this.log(LOG_LEVEL.Trace, functionName,  `Sending DTMF ${data.digits}`);

      if (this.activeCalls.length === 0) {
        throw new Error('No active calls to send DTMF to');
      }

      let activeCall = this.activeCalls[0];
      let activeCallIndex = 0;

      if (this.activeCalls.length > 1) {
        for (let i = 0; i < this.activeCalls.length; i++) {
          if (this.activeCalls[i].callAttributes.isOnHold === false) {
            activeCall = this.activeCalls[i];
            activeCallIndex = i;
            break;
          }
        }
      }

      const activeInteractionId = this.activeParties[activeCallIndex].interactionId;

      this.log(LOG_LEVEL.Debug, functionName, `Sending DTMF ${data.digits} for interactionId: ${activeInteractionId} and phone number: ${activeCall.phoneNumber}`);

      await clickToAct(
        activeCall.phoneNumber,
        undefined,
        undefined,
        CONTEXTUAL_OPERATION_TYPE.DTMF,
        activeInteractionId,
        data.digits
      );
      return;
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Failed to send digits', error);
      throw error;
    }
  }

  /*
   * Logging, Logic, and cleanup functions for the component
   */
  logFromBridge(logObject: {
    logLevel: LOG_LEVEL;
    functionName: string;
    message: string;
    object?: any;
  }): void {
    const functionName = 'Home.' + 'logFromBridge';
    try {
      this.log(
        logObject.logLevel,
        logObject.functionName,
        logObject.message,
        logObject.object
      );
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Failed to log from bridge', {
        logObject,
        error,
      });
    }
  }

  protected removeLocalStorageOnLogout(): string {
    try {
      localStorage.clear();
      return 'Success';
    } catch (error) {
      throw error;
    }
  }

  protected formatAniDnis(interaction: IInteraction): void {
    const functionName = `${this.cname} formatAniDnis`;
    try {
      let phone = '';
      let dialedPhone = '';
      let parties;

      if (interaction.details?.fields.Phone.Value) {
        phone = this.formatPhoneNumber(interaction.details.fields.Phone.Value, this.scs.phoneNumberFormatMap);
        interaction.details.fields.Phone.Value = phone;
      }

      if (interaction.details?.fields.DialedPhone.Value) {
        dialedPhone = this.formatPhoneNumber(interaction.details.fields.DialedPhone.Value, this.scs.phoneNumberFormatMap);
        interaction.details.fields.DialedPhone.Value = dialedPhone;
      }

      if (interaction.details?.fields.conferenceParties?.Value) {
        parties = JSON.parse(interaction.details?.fields?.conferenceParties.Value);
        const keys = Object.keys(parties);
        for (let i = 0; i < keys.length; i++) {
          parties[keys[i]] = this.formatPhoneNumber(parties[keys[i]], this.scs.phoneNumberFormatMap);
        }
        interaction.details.fields.conferenceParties.Value = JSON.stringify(parties);
      }

      this.log(LOG_LEVEL.Debug, functionName, 'Formatted phone numbers: ', {phone, dialedPhone, parties});
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Error formatting ANI and/or DNIS', error);
    }
  }

  protected formatPhoneNumber(
    inputNumber: string,
    phoneNumberFormat: any
  ): string {
    const functionName = `${this.cname} FormatPhoneNumber`;
    try {
      this.log(LOG_LEVEL.Trace, functionName, 'Formatting Phone Number', {
        inputNumber,
        phoneNumberFormat,
      });
      if (Object.keys(phoneNumberFormat).length === 0) {
        return inputNumber;
      }
      const configuredInputFormats = Object.keys(phoneNumberFormat);
      for (const inputFormat of configuredInputFormats) {
        let formatCheck = true;
        const outputFormat = phoneNumberFormat[inputFormat];
        if (typeof outputFormat !== 'string') {
          throw new Error(
            'formatPhoneNumber: outputFormat is not a valid dat type.'
          );
        }
        if (inputFormat.length === inputNumber.length) {
          const arrInputDigits = [];
          let outputNumber = '';
          let outputIncrement = 0;
          if (
            (inputFormat.match(/x/g) || []).length !==
            (outputFormat.match(/x/g) || []).length
          ) {
            continue;
          }
          for (let j = 0; j < inputFormat.length; j++) {
            if (inputFormat[j] === 'x') {
              arrInputDigits.push(j);
            } else if (
              inputFormat[j] !== '?' &&
              inputNumber[j] !== inputFormat[j]
            ) {
              formatCheck = false;
              break;
            }
          }
          if (formatCheck) {
            for (const char of outputFormat) {
              if (char === 'x') {
                outputNumber =
                  outputNumber + inputNumber[arrInputDigits[outputIncrement]];
                outputIncrement++;
              } else {
                outputNumber = outputNumber + char;
              }
            }
            this.log(
              LOG_LEVEL.Trace,
              functionName,
              'Formatted Phone Number',
              outputNumber
            );
            return outputNumber;
          }
        }
      }
    } catch (error) {
      this.log(LOG_LEVEL.Error, functionName, 'Failed to format phone number', {
        inputNumber,
        phoneNumberFormat,
        error,
      });
    }
    return inputNumber;
  }

  protected clickToDialFormatPhoneNumber(number: string): {outputNumber: string; plainNumber: string} {
    if (this.scs.clickToDialPhoneReformatMap === undefined) {
      return { plainNumber: number, outputNumber: number };
    }

    const configuredInputFormats = Object.keys(
      this.scs.clickToDialPhoneReformatMap
    );
    for (const inputFormat of configuredInputFormats) {
      let formatCheck = true;
      if (number.length === inputFormat.length) {
        // Length of incoming number matches length of a configured input format
        // Now Validate # of X's in input/output
        // const outputFormat =
        //   this.scs.clickToDialPhoneReformatMap?.get(inputFormat);

        const outputFormat = this.scs.clickToDialPhoneReformatMap[inputFormat];
        const arrInputDigits = [];
        let outputNumber = '';
        let outputIncrement = 0;
        let plainNumber = '';
        if (
          (inputFormat.match(/x/g) || []).length !==
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          (outputFormat?.match(/x/g) || []).length
        ) {
          continue;
        }
        if (
          (inputFormat.match(/\(/g) || []).length !==
          (number.match(/\(/g) || []).length
        ) {
          continue;
        }
        if (
          (inputFormat.match(/-/g) || []).length !==
          (number.match(/-/g) || []).length
        ) {
          continue;
        }

        for (let j = 0; j < inputFormat.length; j++) {
          if (inputFormat[j] === 'x') {
            arrInputDigits.push(j);
          } else if (inputFormat[j] !== '?' && number[j] !== inputFormat[j]) {
            formatCheck = false;
            break;
          }
        }
        if (formatCheck && outputFormat) {
          for (const char of outputFormat) {
            if (char === 'x') {
              outputNumber =
                outputNumber + number[arrInputDigits[outputIncrement]];
              outputIncrement++;
            } else {
              outputNumber = outputNumber + char;
            }
          }

          // Get the plain number xxxxxxxxxx
          for (const digit of arrInputDigits) {
            plainNumber += digit;
          }


          return {
            plainNumber,
            outputNumber
          };
        }
      }
    }
    return {
      plainNumber: number,
      outputNumber: number
    };
  }

  /**
   * This function updates the contact information at the provided index in the activeCalls and activeParties lists.
   *
   * @private
   * @param {CID} cId The unique identifier tying the invocation of this function back to its parent process
   * @param {number} partyIndex The index in the two lists to update
   * @param {string} newContact The new string to be used as the displayed contact information
   * @param {string} [interactionId] Optionally update the interactionId on the party
   * @param {string} [scenarioId] Optionally update the scenarioId on the party
   * @memberof HomeBYOTComponent
   */
  private updateContact(cId: CID, partyIndex: number, newContact: string, interactionId?: string, scenarioId?: string, isOnHold?: boolean) {
    const fname = `updateContact(${cId})`;
    try {
      this.log(LOG_LEVEL.Debug, fname, `Attempting to update party/call at index ${partyIndex} to new contact ${newContact}`, { originalParty: this.activeParties[partyIndex] });
      this.activeParties[partyIndex].name = newContact;
      this.activeParties[partyIndex].partyId = newContact;
      this.activeParties[partyIndex].phoneNumber = newContact;

      this.activeCalls[partyIndex].contact.name = newContact;
      this.activeCalls[partyIndex].contact.id = newContact;
      this.activeCalls[partyIndex].contact.phoneNumber = newContact;
      this.activeCalls[partyIndex].phoneNumber = newContact;

      if (interactionId) {
        this.log(LOG_LEVEL.Loop, fname, `Also updating interactionId on the party to ${interactionId}`);
        this.activeParties[partyIndex].interactionId = interactionId;
      }

      if (scenarioId) {
        this.log(LOG_LEVEL.Loop, fname, `Also updating scenarioId on the party to ${scenarioId}`);
        this.activeParties[partyIndex].scenarioId = scenarioId;
      }

      if (isOnHold !== undefined) {
        this.log(LOG_LEVEL.Loop, fname, `Also updating held status to ${isOnHold}`);
        this.activeCalls[partyIndex].callAttributes.isOnHold = isOnHold;
        this.activeCalls[partyIndex].callInfo.isOnHold = isOnHold;
      }

      this.log(LOG_LEVEL.Debug, fname, `Successfully updated party/call at index ${partyIndex}`, { newParty: this.activeParties[partyIndex] });
    } catch (error) {
      this.log(LOG_LEVEL.Error, fname, `Failed to update party/call at index ${partyIndex} to new contact ${newContact}`);
    }
  }

  /*
   * Default DaVinci functions used to control softphone settings for a CRM
   * Not currently implemented
   */
  async isToolbarVisible(): Promise<boolean> {
    return Boolean(
      await this.bridgeEventsService?.sendEvent('isToolbarVisible')
    );
  }

  // eslint-disable-next-line @typescript-eslint/require-await, @typescript-eslint/no-unused-vars
  protected async saveActivity(activity: IActivity): Promise<string> {
    throw new Error('Method not implemented.');
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  protected async getSearchLayout(): Promise<SearchLayouts> {
    throw new Error('Method not implemented.');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  protected formatCrmResults(crmResults: any): SearchRecords {
    throw new Error('Method not implemented.');
  }

  getBridgeURL(): string {
    let bridgeUrl = `${window.location.origin}/bridge.bundle.js`;
    const mainScriptElement = document.querySelector(
      'script[src^="/main."][src$="bundle.js"]'
    );

    if (!(mainScriptElement instanceof HTMLScriptElement)) {
      throw new Error('getBridgeURL: did not receive a <script> element.');
    }

    const hash = mainScriptElement.src.replace(
      /.*main\.([a-zA-Z0-9]+)\.bundle\.js/,
      '$1'
    );
    if (hash !== mainScriptElement.src) {
      bridgeUrl = `${window.location.origin}/bridge.${hash}.bundle.js`;
    }
    return this.addQueryParam(bridgeUrl, 'appname', this.appName || '');
  }

  addQueryParam(url: string, paramName: string, paramValue: string): string {
    if (!url.includes('?')) {
      url += '?';
    } else {
      url += '&';
    }
    url += paramName + '=' + encodeURIComponent(paramValue);
    return url;
  }

  async parentNgOnInit(): Promise<void> {
    // Initialize the service used to communicate with the bridge
    this.bridgeEventsService = new BridgeEventsService();
    this.bridgeEventsService.init(await getAppName());

    // This is used to give the bridge a way to save logs.
    this.bridgeEventsService.subscribe('logError', (event) => {
      this.bridgeLogger.logError(event);
    });
    this.bridgeEventsService.subscribe('logWarning', (event) => {
      this.bridgeLogger.logWarning(event);
    });
    this.bridgeEventsService.subscribe('logInformation', (event) => {
      this.bridgeLogger.logInformation(event);
    });
    this.bridgeEventsService.subscribe('logCriticial', (event) => {
      this.bridgeLogger.logCriticial(event);
    });
    this.bridgeEventsService.subscribe('logLoop', (event) => {
      this.bridgeLogger.logLoop(event);
    });
    this.bridgeEventsService.subscribe('logTrace', (event) => {
      this.bridgeLogger.logTrace(event);
    });
    this.bridgeEventsService.subscribe('logDebug', (event) => {
      this.bridgeLogger.logDebug(event);
    });

    // this.bridgeEventsService?.subscribe('onFocus', this.boundOnFocusHandler);

    await this.registerForAmcEvents();

    if (this.debugMode) {
      setAppHeight(500);
    }
    await initializeComplete(this.logger);
  }

  protected async registerForAmcEvents(): Promise<void> {
    const setupPromises: Promise<any>[] = [
      loadBridgeScripts(this.bridgeScripts).then(async () => {
        await this.bridgeEventsService?.sendEvent('afterScriptsLoad');
      }),
      registerOnInteraction(this.onInteraction.bind(this)),
      registerEnableClickToDial(this.enableClickToDial.bind(this)),
      registerGetSearchLayout(this.getSearchLayout.bind(this)),
      registerIsToolbarVisible(this.isToolbarVisible.bind(this)),
      registerOnPresenceChanged(this.receivePresenceFromFramework.bind(this)),
      registerOnLogout(this.logoutCallback.bind(this)),
      registerSetSupportedChannels(this.getChannelConfig.bind(this))
    ];

    for (const promise of setupPromises) {
      await promise;
    }
    return;
  }

  async enableClickToDial(clickToDialEnabled: boolean): Promise<any> {
    return await this.bridgeEventsService?.sendEvent(
      'enableClickToDial',
      clickToDialEnabled
    );
  }
}
